// pages/TableMaintenance.jsx
// Drop-in page untuk dashboard existing.
// Pakai axios instance + token dari AuthContext (otomatis via interceptor).

import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import useTabDeepLink from '../hooks/useTabDeepLink';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { useAuth, api as axiosApi } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
// Primitif UI + CronScheduler kini dibagi dengan MonthlyReport.jsx.
import { Alert, Badge, Btn, inputStyle, Empty, useApi, fmtBytes, cronLocalTime, CronScheduler } from './tmShared';
import AseHealthPanel from '../components/AseHealthPanel';

// --- AWS multi-account for the AWS Billing / AWS Cost panels ---
// Every /api/billing/* and /api/aws/* call from these panels carries ?account=<selected key>
// explicitly, so the table view, export, upload, fetch and clear all act on the SAME
// account that is shown in the account bar (never an implicit 'primary' fallback).
const tmAwsAcctKey = () => { try { return localStorage.getItem('awsinv.account') || 'primary'; } catch (_) { return 'primary'; } };
const withAwsAcct = (path) => {
  if (typeof path !== 'string' || !/^\/api\/(billing|aws)\//.test(path) || /[?&]account=/.test(path)) return path;
  return path + (path.includes('?') ? '&' : '?') + 'account=' + encodeURIComponent(tmAwsAcctKey());
};
function AwsAccountBar({ api }) {
  const [accts, setAccts] = useState([]);
  useEffect(() => { api('/api/aws/accounts').then((d) => setAccts(d.accounts || [])).catch(() => setAccts([])); }, [api]);
  const key = tmAwsAcctKey();
  const cur = accts.find((a) => a.key === key);
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, margin:'0 0 10px', padding:'8px 12px', borderRadius:10, background:'rgb(var(--brand-50))', border:'1px solid rgb(var(--brand-200))', flexWrap:'wrap' }}>
      <span style={{ fontSize:12, color:'#475467' }}>AWS account:</span>
      <select value={key} onChange={(e) => { try { localStorage.setItem('awsinv.account', e.target.value); } catch (_) {} window.location.reload(); }}
        style={{ padding:'4px 10px', borderRadius:8, border:'1px solid #d0d5dd', background:'#fff', fontSize:13, fontWeight:700 }}>
        {accts.length ? accts.map((a) => <option key={a.key} value={a.key}>{a.label}</option>) : <option value={key}>{key}</option>}
      </select>
      {cur && cur.accountId && <span style={{ fontSize:12, color:'#667085' }}>ID {cur.accountId}</span>}
      <span style={{ fontSize:12, color:'#667085' }}>— data, upload, fetch and clear below apply to this account only.</span>
    </div>
  );
}

// --- AWS multi-account guard for "Clear Table" (AWS Billing / AWS Cost) ---
// The active account is shared with the AWS Billing / Cost Explorer / Inventory pages
// (localStorage 'awsinv.account'). Clearing ALWAYS sends ?account=<key> explicitly —
// the backend falls back to 'primary' when no account is given, which previously
// wiped PIL data even while another account was selected.
// The user must type the account name to confirm. Returns the account object, or null.
async function confirmAwsClear(api, kind) {
  let key = 'primary';
  try { key = localStorage.getItem('awsinv.account') || 'primary'; } catch (_) {}
  let accts = [];
  try { accts = (await api('/api/aws/accounts')).accounts || []; } catch (e) {
    window.alert('Cannot load AWS account list — clear cancelled.\n' + e.message); return null;
  }
  const acct = accts.find((a) => a.key === key);
  if (!acct) { window.alert('Selected AWS account "' + key + '" is not registered — clear cancelled.'); return null; }
  const q = '?account=' + encodeURIComponent(acct.key);
  let n = '?';
  try {
    if (kind === 'billing') n = (await api('/api/billing/status' + q)).count;
    else n = (await api('/api/aws/cost-raw' + q + '&limit=1')).total;
  } catch (_) {}
  const table = kind === 'billing' ? 'AWS Billing' : 'AWS Cost';
  const others = accts.filter((a) => a.key !== acct.key).map((a) => a.label).join(', ') || '-';
  const typed = window.prompt(
    'DELETE ALL ' + table + ' data for:\n\n' +
    '   ' + acct.label + (acct.accountId ? ' (' + acct.accountId + ')' : '') + '\n' +
    '   ' + n + ' rows — this cannot be undone.\n\n' +
    'Other accounts are NOT affected: ' + others + '.\n\n' +
    'Type the account name exactly to confirm:\n' + acct.label
  );
  if (typed === null) return null;
  if (typed.trim() !== acct.label) { window.alert('Account name does not match — nothing was deleted.'); return null; }
  return acct;
}


// ── Utilities ─────────────────────────────────────────────────────────────────

function shortVal(v) {
  if (v === null || v === undefined) return '—';
  const s = String(v);
  return s.length > 42 ? s.slice(0, 40) + '…' : s;
}
function fmt(n) { return Number(n || 0).toLocaleString(); }

// ── Export ke Excel (.xlsx) ─────────────────────────────────────────────────────
// Ambil SELURUH baris dengan auto-loop pagination. pageSize HARUS ≤ cap server
// (Billing dibatasi 500); kalau lebih, deteksi "halaman terakhir" jadi salah.
async function fetchAllPaged(api, baseUrl, field, { pageSize = 5000, search = '' } = {}) {
  const sq = search ? `&search=${encodeURIComponent(search)}` : '';
  const sep = baseUrl.includes('?') ? '&' : '?';
  const out = [];
  for (let p = 0; p < 5000; p++) {
    const d = await api(`${baseUrl}${sep}limit=${pageSize}&offset=${p * pageSize}${sq}`);
    const batch = (d && d[field]) || [];
    out.push(...batch);
    if (batch.length < pageSize) break;
  }
  return out;
}

// Bangun .xlsx dari rows + definisi kolom ({key,label,numeric}) lalu unduh.
function exportRowsToXlsx(rows, cols, filename, sheetName = 'Sheet1') {
  const header = cols.map(c => c.label);
  const aoa = [header, ...rows.map(r => cols.map(c => {
    const v = r[c.key];
    if (c.numeric || c.qty) { const n = parseFloat(v); return Number.isFinite(n) ? n : (v ?? ''); }
    return v ?? '';
  }))];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, String(sheetName).slice(0, 31));
  XLSX.writeFile(wb, filename);
}

// Tombol export reusable. onExport: async () => void (lempar error jika gagal).
function ExportXlsxButton({ onExport, onError, label = '\u2B07\uFE0F Export Excel' }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await onExport(); }
    catch (e) { onError ? onError(e) : alert('Export gagal: ' + (e.message || e)); }
    finally { setBusy(false); }
  };
  return <Btn size="sm" disabled={busy} onClick={run}>{busy ? '\u23F3 Menyiapkan\u2026' : label}</Btn>;
}

// ── Shared UI ─────────────────────────────────────────────────────────────────


function StatCard({ label, value }) {
  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', minWidth: 130 }}>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>{value}</div>
    </div>
  );
}


function Tag({ text }) {
  return <span style={{ background: '#f1f5f9', color: '#475569', borderRadius: 4, padding: '2px 6px', fontSize: 11, fontFamily: 'monospace' }}>{text}</span>;
}

function Th({ children }) {
  return <th style={{ background: '#f8fafc', padding: '9px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#64748b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{children}</th>;
}
function Td({ children, title }) {
  return <td title={title} style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#1e293b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children ?? '—'}</td>;
}

function Modal({ title, children, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: '#0f172a' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4, fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

function Input(props) { return <input {...props} style={inputStyle} />; }
function Select({ children, ...props }) { return <select {...props} style={{ ...inputStyle, background: '#fff' }}>{children}</select>; }

function Loading() {
  const {
    t
  } = useLang();

  return (
    <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>
      {t('tblMaint.memuatData')}
    </div>
  );
}

// ── useApi — pakai axios instance dari AuthContext (token otomatis via interceptor) ──

// ── Upload CSV Button (reusable) ───────────────────────────────────────────────
// mode 'multipart' : kirim file langsung ke backend (parsing di server)
// mode 'json'      : parse CSV/JSON di browser (PapaParse) lalu kirim rows sebagai JSON
function UploadCsvButton({ mode, endpoint, field = 'file', label = '\uD83D\uDCE4 Upload CSV', jsonBuilder, onDone }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // reset agar file yang sama bisa diupload ulang
    if (!file) return;
    setBusy(true);
    try {
      if (mode === 'multipart') {
        const fd = new FormData();
        fd.append(field, file);
        await axiosApi.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        onDone && onDone({ ok: true, msg: `File "${file.name}" berhasil diupload.` });
      } else {
        const text = await file.text();
        const ext = file.name.split('.').pop().toLowerCase();
        let rows = [];
        if (ext === 'json') {
          const p = JSON.parse(text);
          rows = Array.isArray(p) ? p : [p];
        } else if (ext === 'csv') {
          rows = await new Promise((resolve, reject) => {
            Papa.parse(text, {
              header: true, skipEmptyLines: true, dynamicTyping: false,
              complete: (r) => resolve(r.data), error: reject,
            });
          });
        } else {
          throw new Error('Format tidak didukung. Gunakan .csv atau .json');
        }
        if (!rows.length) throw new Error('File kosong / tidak ada baris valid.');
        const body = jsonBuilder ? jsonBuilder(rows, file.name) : { rows };
        await axiosApi.post(endpoint, body);
        onDone && onDone({ ok: true, msg: `${rows.length} baris dari "${file.name}" berhasil diupload.` });
      }
    } catch (err) {
      const m = (err.response && err.response.data && err.response.data.error) || err.message || 'Upload gagal.';
      onDone && onDone({ ok: false, msg: m });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept=".csv,.json" style={{ display: 'none' }} onChange={handleFile} />
      <Btn size="sm" variant="primary" disabled={busy} onClick={() => inputRef.current && inputRef.current.click()}>
        {busy ? '\u23F3 Uploading\u2026' : label}
      </Btn>
    </>
  );
}

// ── Panels ────────────────────────────────────────────────────────────────────

function UsersPanel({ api }) {
  const [users, setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]       = useState(null);
  const [modal, setModal]   = useState(null);
  const [form, setForm]     = useState({});
  const { user: me }        = useAuth();
  const { t } = useLang();
  const ti = (key, vars) => {
    let s = t(key);
    if (vars) for (const k in vars) s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), vars[k]);
    return s;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await api('/api/users'); setUsers(d.users || []); }
    catch (e) { setMsg({ text: e.message, type: 'error' }); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const deleteUser = async (id, username) => {
    if (!window.confirm(ti('users.deleteConfirm', { name: username }))) return;
    try {
      await api('/api/users/' + id, { method: 'DELETE' });
      setMsg({ text: ti('users.deleted', { name: username }), type: 'success' });
      load();
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  };

  const saveNew = async () => {
    try {
      await api('/api/users', { method: 'POST', body: JSON.stringify({ username: form.username, password: form.password, role: form.role || 'SAP' }) });
      setMsg({ text: ti('users.created', { name: form.username }), type: 'success' });
      setModal(null); load();
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  };

  const saveEdit = async () => {
    const body = { role: form.newRole };
    if (form.newPw) body.password = form.newPw;
    try {
      await api('/api/users/' + modal.id, { method: 'PUT', body: JSON.stringify(body) });
      setMsg({ text: t('users.updated'), type: 'success' });
      setModal(null); load();
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  };

  const roleColor = { admin: '#dc2626', SAP: 'rgb(var(--brand-600))', AWS: '#d97706' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{t('tm.tab.users')}</span>
          <Badge text={ti('users.rows', { n: users.length })} color="rgb(var(--brand-600))" />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <ExportXlsxButton
            onExport={() => exportRowsToXlsx(users, [
              { key: 'id', label: 'ID' }, { key: 'username', label: 'Username' }, { key: 'role', label: 'Role' },
              { key: 'last_login', label: 'Last Login' }, { key: 'created_at', label: 'Created' },
            ], 'users.xlsx', 'Users')}
            onError={e => setMsg({ text: e.message, type: 'error' })} />
          <Btn variant="primary" onClick={() => { setForm({ username: '', password: '', role: 'SAP' }); setModal('add'); }}>➕ {t('users.addBtn')}</Btn>
        </div>
      </div>
      <Alert msg={msg?.text} type={msg?.type} onClose={() => setMsg(null)} />
      {loading ? <Loading /> : (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><Th>{t('users.colId')}</Th><Th>{t('users.colUsername')}</Th><Th>{t('users.colRole')}</Th><Th>{t('users.colLastLogin')}</Th><Th>{t('users.colCreated')}</Th><Th>{t('common.actions')}</Th></tr></thead>
            <tbody>
              {users.length === 0 && <tr><td colSpan={6}><Empty icon="👥" text={t('users.empty')} /></td></tr>}
              {users.map(u => (
                <tr key={u.id}>
                  <Td>{u.id}</Td>
                  <Td><strong>{u.username}</strong>{u.id === me?.id && <span style={{ marginLeft: 6, fontSize: 10, color: 'rgb(var(--brand-600))', fontWeight: 600 }}>{t('users.you')}</span>}</Td>
                  <Td><Badge text={u.role} color={roleColor[u.role] || '#64748b'} /></Td>
                  <Td title={u.last_login}>{shortVal(u.last_login)}</Td>
                  <Td title={u.created_at}>{shortVal(u.created_at)}</Td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn size="sm" onClick={() => { setForm({ newRole: u.role, newPw: '' }); setModal({ edit: true, id: u.id, username: u.username }); }}>✏️ {t('common.edit')}</Btn>
                      {u.id !== me?.id && <Btn size="sm" variant="danger" onClick={() => deleteUser(u.id, u.username)}>🗑️</Btn>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal === 'add' && (
        <Modal title={'➕ ' + t('users.modalAdd')} onClose={() => setModal(null)}>
          <Field label={t('users.colUsername')}><Input value={form.username || ''} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder={t('users.phUsername')} /></Field>
          <Field label={t('users.fPassword')}><Input type="password" value={form.password || ''} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder={t('users.phPassword')} /></Field>
          <Field label={t('users.colRole')}><Select value={form.role || 'SAP'} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}><option value="admin">admin</option><option value="SAP">SAP</option><option value="AWS">AWS</option></Select></Field>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <Btn onClick={() => setModal(null)}>{t('common.cancel')}</Btn>
            <Btn variant="primary" onClick={saveNew}>{t('users.btnCreate')}</Btn>
          </div>
        </Modal>
      )}
      {modal?.edit && (
        <Modal title={'✏️ ' + ti('users.modalEdit', { name: modal.username })} onClose={() => setModal(null)}>
          <Field label={t('users.fNewRole')}><Select value={form.newRole} onChange={e => setForm(f => ({ ...f, newRole: e.target.value }))}><option value="admin">admin</option><option value="SAP">SAP</option><option value="AWS">AWS</option></Select></Field>
          <Field label={t('users.fNewPw')}><Input type="password" value={form.newPw || ''} onChange={e => setForm(f => ({ ...f, newPw: e.target.value }))} placeholder="••••••" /></Field>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <Btn onClick={() => setModal(null)}>{t('common.cancel')}</Btn>
            <Btn variant="primary" onClick={saveEdit}>{t('common.save')}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function LogsPanel({ api }) {
  const [logs, setLogs]   = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const { t } = useLang();
  const ti = (key, vars) => {
    let s = t(key);
    if (vars) for (const k in vars) s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), vars[k]);
    return s;
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const d = await api('/api/logs?limit=100'); setLogs(d.logs || []); setTotal(d.total || 0); }
      catch {}
      finally { setLoading(false); }
    })();
  }, [api]);

  const filtered = logs.filter(l =>
    !search || [l.username, l.action, l.detail, l.role].some(v => v && String(v).toLowerCase().includes(search.toLowerCase()))
  );
  const roleColor = { admin: '#dc2626', SAP: 'rgb(var(--brand-600))', AWS: '#d97706' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{t('tm.tab.logs')}</span>
          <Badge text={ti('logs.total', { n: total })} color="#64748b" />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={'🔍 ' + t('logs.searchPh')} style={{ ...inputStyle, width: 220 }} />
          <ExportXlsxButton onExport={async () => {
            const all = await fetchAllPaged(api, '/api/logs', 'logs', { pageSize: 5000 });
            const f = all.filter(l => !search || [l.username, l.action, l.detail, l.role].some(v => v && String(v).toLowerCase().includes(search.toLowerCase())));
            exportRowsToXlsx(f, [
              { key: 'id', label: 'ID' }, { key: 'username', label: 'Username' }, { key: 'role', label: 'Role' },
              { key: 'action', label: 'Action' }, { key: 'detail', label: 'Detail' }, { key: 'ip_address', label: 'IP' }, { key: 'created_at', label: 'Waktu' },
            ], 'activity_logs.xlsx', 'Logs');
          }} />
        </div>
      </div>
      <div style={{ fontSize: 12, color: '#92400e', marginBottom: 12, padding: '8px 12px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a' }}>
        ℹ️ {ti('logs.info', { n: fmt(total) })}
      </div>
      {loading ? <Loading /> : (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><Th>{t('users.colId')}</Th><Th>{t('users.colUsername')}</Th><Th>{t('users.colRole')}</Th><Th>{t('logs.colAction')}</Th><Th>{t('logs.colDetail')}</Th><Th>{t('logs.colIp')}</Th><Th>{t('logs.colTime')}</Th></tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={7}><Empty icon="📋" text={t('logs.empty')} /></td></tr>}
              {filtered.map(l => (
                <tr key={l.id}>
                  <Td>{l.id}</Td>
                  <Td><strong>{l.username}</strong></Td>
                  <Td><Badge text={l.role} color={roleColor[l.role] || '#64748b'} /></Td>
                  <Td><Tag text={l.action} /></Td>
                  <Td title={l.detail}>{shortVal(l.detail)}</Td>
                  <Td>{l.ip_address || '—'}</Td>
                  <Td>{shortVal(l.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TicketsPanel({ api }) {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal]     = useState(0);
  const [sources, setSources] = useState([]);
  const [selSrc, setSelSrc]   = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]         = useState(null);
  const [page, setPage]       = useState(0);
  const [search, setSearch]   = useState('');
  const [fetchModal, setFetchModal] = useState(false);
  const [fetchBusy, setFetchBusy]   = useState(false);
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo]     = useState('');
  const [fType, setFType] = useState('ZMIN');
  const PAGE = 100;
  const { t } = useLang();
  const ti = (key, vars) => {
    let s = t(key);
    if (vars) for (const k in vars) s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), vars[k]);
    return s;
  };

  const load = useCallback(async (p = 0, q = '') => {
    setLoading(true);
    try {
      const sq = q ? `&search=${encodeURIComponent(q)}` : '';
      const [d, s] = await Promise.all([
        api(`/api/tickets?limit=${PAGE}&offset=${p * PAGE}${sq}`),
        api('/api/tickets/sources'),
      ]);
      setTickets(d.tickets || []);
      setTotal(s.total || d.total || 0);
      setSources(s.sources || []);
      setPage(p);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(0); }, [load]);

  const handleSearch = (val) => { setSearch(val); setPage(0); load(0, val); };

  const deleteSource = async () => {
    if (!selSrc) { setMsg({ text: t('tickets.pickSource'), type: 'error' }); return; }
    if (!window.confirm(ti('tickets.deleteSourceConfirm', { name: selSrc }))) return;
    try {
      const r = await api('/api/tickets', { method: 'DELETE', body: JSON.stringify({ sourceFile: selSrc }) });
      setMsg({ text: r.message, type: 'success' });
      setSelSrc(''); load(0);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  };

  const deleteAll = async () => {
    if (!window.confirm(ti('tickets.deleteAllConfirm', { n: fmt(total) }))) return;
    try {
      const r = await api('/api/tickets', { method: 'DELETE', body: JSON.stringify({}) });
      setMsg({ text: r.message, type: 'success' });
      setSelSrc(''); load(0);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  };

  const fetchSolman = async () => {
    const ymd = /^\d{8}$/;
    if (fFrom && !ymd.test(fFrom)) { setMsg({ text: t('tickets.errFrom'), type: 'error' }); return; }
    if (fTo && !ymd.test(fTo))     { setMsg({ text: t('tickets.errTo'), type: 'error' }); return; }
    setFetchBusy(true);
    try {
      const r = await api('/api/tickets/fetch-solman', {
        method: 'POST',
        body: JSON.stringify({ from: fFrom, to: fTo, type: fType || 'ZMIN' }),
      });
      setMsg({ text: r.message || t('tickets.fetchDone'), type: 'success' });
      setFetchModal(false);
      load(0);
    } catch (e) {
      setMsg({ text: e.message || t('tickets.fetchFail'), type: 'error' });
    } finally { setFetchBusy(false); }
  };

  const prioColor = p => ({ 'Very High': '#dc2626', High: '#ea580c', Medium: '#d97706' }[p] || '#64748b');
  const totalPages = Math.ceil(total / PAGE);

  // Semua kolom sesuai tabel solman_tickets di database
  const COLS = [
    { key: 'ticket_id',          label: 'Ticket ID',          sticky: true },
    { key: 'company',            label: 'Company' },
    { key: 'priority',           label: 'Priority' },
    { key: 'status',             label: 'Status' },
    { key: 'description',        label: 'Description' },
    { key: 'impact',             label: 'Impact' },
    { key: 'created_on',         label: 'Created On' },
    { key: 'created_on2',        label: 'Created On2' },
    { key: 'reporter',           label: 'Reporter' },
    { key: 'processor',          label: 'Processor' },
    { key: 'service_team',       label: 'Service Team' },
    { key: 'system_id',          label: 'System ID' },
    { key: 'system_status',      label: 'System Status' },
    { key: 'transaction_type',   label: 'Transaction Type' },
    { key: 'client',             label: 'Client' },
    { key: 'transaction_type_id',label: 'Trans Type ID' },
    { key: 'status_id',          label: 'Status ID' },
    { key: 'trans_category',     label: 'Trans Category' },
    { key: 'system',             label: 'System' },
    { key: 'sap_installation',   label: 'SAP Installation' },
    { key: 'software_release',   label: 'Software Release' },
    { key: 'sap_component',      label: 'SAP Component' },
    { key: 'month_year',         label: 'Month Year' },
    { key: 'year_num',           label: 'Year' },
    { key: 'month_num',          label: 'Month' },
    { key: 'created_by',         label: 'Created By' },
    { key: 'uploaded_by',        label: 'Uploaded By' },
    { key: 'uploaded_at',        label: 'Uploaded At' },
    { key: 'source_file',        label: 'Source File' },
  ];

  const stickyTh = { background: '#f8fafc', padding: '9px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#64748b', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em', position: 'sticky', left: 0, zIndex: 2, boxShadow: '2px 0 4px rgba(0,0,0,0.06)' };
  const stickyTd = { padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#0f172a', fontWeight: 700, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: '#fff', zIndex: 1, boxShadow: '2px 0 4px rgba(0,0,0,0.04)' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label={t('tickets.statTotal')} value={fmt(total)} />
        <StatCard label={t('tickets.statSources')} value={sources.length} />
        <StatCard label={t('tickets.statPage')} value={(page + 1) + ' / ' + (totalPages || 1)} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{t('tm.tab.tickets')}</span>
          <Badge text={ti('users.rows', { n: fmt(total) })} color="rgb(var(--brand-600))" />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={search} onChange={e => handleSearch(e.target.value)} placeholder={'🔍 ' + t('tickets.searchPh')} style={{ ...inputStyle, width: 220 }} />
          <ExportXlsxButton
            onExport={async () => { const all = await fetchAllPaged(api, '/api/tickets', 'tickets', { search }); exportRowsToXlsx(all, COLS, 'solman_tickets.xlsx', 'Solman Tickets'); }}
            onError={e => setMsg({ text: e.message, type: 'error' })} />
          <UploadCsvButton mode="multipart" endpoint="/api/tickets/bulk-stream"
            onDone={r => { setMsg({ text: r.msg, type: r.ok ? 'success' : 'error' }); if (r.ok) load(0); }} />
          <Btn size="sm" variant="primary" onClick={() => setFetchModal(true)}>📡 {t('tickets.btnFetch')}</Btn>
          <select value={selSrc} onChange={e => setSelSrc(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 200 }}>
            <option value="">{t('tickets.selectSource')}</option>
            {sources.map(s => <option key={s.source_file} value={s.source_file}>{s.source_file} ({ti('users.rows', { n: fmt(s.rows) })})</option>)}
          </select>
          <Btn size="sm" variant="danger" onClick={deleteSource}>🗑️ {t('tickets.btnDelSource')}</Btn>
          <Btn size="sm" variant="warning" onClick={deleteAll}>⚠️ {t('tickets.btnClearAll')}</Btn>
        </div>
      </div>
      <Alert msg={msg?.text} type={msg?.type} onClose={() => setMsg(null)} />
      <CronScheduler api={api} jobKey="solman_tickets"
        runConfirm={t('tickets.cronConfirm')} />
      {/* Job kedua di tab yang sama: export bulanan ke SharePoint. Sengaja
          diletakkan tepat di bawah cron fetch karena keduanya berurutan —
          fetch mengisi solman_tickets, export membacanya. Menaruhnya di
          halaman lain akan memutus kaitan itu secara visual. */}
      <CronScheduler api={api} jobKey="solman_sharepoint"
        runConfirm={t('tickets.spCronConfirm')} />
      {fetchModal && (
        <Modal title={'📡 ' + t('tickets.modalTitle')} onClose={() => !fetchBusy && setFetchModal(false)}>
          <Field label={t('tickets.fType')}>
            <Input value={fType} onChange={e => setFType(e.target.value.toUpperCase())} placeholder="ZMIN" />
          </Field>
          <Field label={t('tickets.fFrom')}>
            <Input value={fFrom} onChange={e => setFFrom(e.target.value.trim())} placeholder="20160101" />
          </Field>
          <Field label={t('tickets.fTo')}>
            <Input value={fTo} onChange={e => setFTo(e.target.value.trim())} placeholder="20160131" />
          </Field>
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 12, lineHeight: 1.5 }}>
            {t('tickets.fetchInfo')}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn size="sm" onClick={() => setFetchModal(false)} disabled={fetchBusy}>{t('common.cancel')}</Btn>
            <Btn size="sm" variant="primary" onClick={fetchSolman} disabled={fetchBusy}>
              {fetchBusy ? '⏳ ' + t('tickets.fetching') : '📡 ' + t('tickets.btnFetchNow')}
            </Btn>
          </div>
        </Modal>
      )}
      {loading ? <Loading /> : (
        <>
          <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e2e8f0', maxHeight: 520, overflowY: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: 'max-content' }}>
              <thead>
                <tr>
                  {COLS.map(c => c.sticky
                    ? <th key={c.key} style={stickyTh}>{c.label}</th>
                    : <Th key={c.key}>{c.label}</Th>
                  )}
                </tr>
              </thead>
              <tbody>
                {tickets.length === 0 && (
                  <tr><td colSpan={COLS.length}><Empty icon="🎫" text={t('tickets.empty')} /></td></tr>
                )}
                {tickets.map((r, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    {COLS.map(c => {
                      if (c.sticky) return <td key={c.key} style={stickyTd}>{r[c.key] || '—'}</td>;
                      if (c.key === 'priority') return (
                        <Td key={c.key}>
                          <span style={{ color: prioColor(r.priority), fontWeight: 700, fontSize: 12 }}>{r.priority || '—'}</span>
                        </Td>
                      );
                      if (c.key === 'source_file') return <Td key={c.key}><Tag text={shortVal(r.source_file)} /></Td>;
                      return <Td key={c.key} title={String(r[c.key] || '')}>{shortVal(r[c.key])}</Td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', justifyContent: 'center' }}>
              <Btn size="sm" disabled={page === 0} onClick={() => load(page - 1)}>← {t('tickets.prev')}</Btn>
              <span style={{ fontSize: 13, color: '#64748b' }}>
                {ti('tickets.pager', { p: page + 1, tp: totalPages, a: fmt(tickets.length), b: fmt(total) })}
              </span>
              <Btn size="sm" disabled={page >= totalPages - 1} onClick={() => load(page + 1)}>{t('tickets.next')} →</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── SAP Usage: helper periode ──
function sapPeriodRange(v) {
  const pad = (n) => String(n).padStart(2, '0');
  const lastDay = (y, mo) => new Date(y, mo, 0).getDate();
  let m = /^(\d{4})-(\d{2})$/.exec(v || '');
  if (m) { const y = +m[1], mo = +m[2]; return { from: `${y}-${pad(mo)}-01`, to: `${y}-${pad(mo)}-${pad(lastDay(y, mo))}` }; }
  m = /^(\d{4})-T([123])$/.exec(v || '');
  if (m) { const y = +m[1], s = (+m[2] - 1) * 4 + 1, e = s + 3; return { from: `${y}-${pad(s)}-01`, to: `${y}-${pad(e)}-${pad(lastDay(y, e))}` }; }
  return null;
}
function sapRecentMonths(n) {
  const out = []; const d = new Date(); d.setDate(1);
  for (let i = 0; i < n; i++) { out.unshift(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); d.setMonth(d.getMonth() - 1); }
  return out;
}
const SAP_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function sapPeriodOptions() {
  const y = new Date().getFullYear();
  const tri = [];
  for (const yy of [y, y - 1]) for (const tt of [3, 2, 1]) { const s = (tt - 1) * 4; tri.push({ v: `${yy}-T${tt}`, label: `Trimester ${yy}-T${tt} (${SAP_MONTHS[s]}–${SAP_MONTHS[s + 3]} ${yy})` }); }
  const months = sapRecentMonths(12).reverse().map((v) => ({ v, label: `Month ${SAP_MONTHS[+v.slice(5) - 1]} ${v.slice(0, 4)}` }));
  return [...months, ...tri];
}
const isPrdConn = (c) => String(c?.environment || '').toUpperCase() === 'PRD';

function SapPanel({ api }) {
  // ── Fetch langsung dari SAP (ZRFC_USR_UTL) ──
  const [sapModal, setSapModal] = useState(false);
  const [sapCov, setSapCov] = useState([]);
  const [sapPeriodPick, setSapPeriodPick] = useState('');
  const loadCov = async () => { try { const r = await api('/api/sap-usage/coverage'); setSapCov(Array.isArray(r?.rows) ? r.rows : []); } catch { setSapCov([]); } };
  useEffect(() => { loadCov(); }, []);
  const pickSapPeriod = (v) => { setSapPeriodPick(v); const pr = sapPeriodRange(v); if (pr) setSapForm((f) => ({ ...f, dateFrom: pr.from, dateTo: pr.to })); };
  const [sapConns, setSapConns] = useState([]);
  const [sapBusy, setSapBusy]   = useState(false);
  const todayISO = new Date().toISOString().slice(0, 10);
  const [sapForm, setSapForm]   = useState({ connectionId: '', uname: 'PG*', department: '', dateFrom: todayISO.slice(0, 8) + '01', dateTo: todayISO, soapPath: '/sap/bc/soap/rfc', mode: 'replace' });

  const [stat, setStat]     = useState({});
  const { t } = useLang();
  const ti = (key, vars) => {
    let s = t(key);
    if (vars) for (const k in vars) s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), vars[k]);
    return s;
  };
  const [rows, setRows]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [selSrc, setSelSrc] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]       = useState(null);
  const [page, setPage]     = useState(0);
  const [search, setSearch] = useState('');
  const PAGE = 100;

  const load = useCallback(async (p = 0, q = '') => {
    setLoading(true);
    try {
      const sq = q ? `&search=${encodeURIComponent(q)}` : '';
      const [s, d] = await Promise.all([
        api('/api/sap-usage/status'),
        api(`/api/sap-usage/data?limit=${PAGE}&offset=${p * PAGE}${sq}`),
      ]);
      setStat(s); setRows(d.data || []); setTotal(d.total || 0); setPage(p);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(0); }, [load]);

  const handleSearch = (val) => { setSearch(val); setPage(0); load(0, val); };

  const delSrc = async () => {
    if (!selSrc) { setMsg({ text: t('tickets.pickSource'), type: 'error' }); return; }
    if (!window.confirm(ti('sap.delSourceConfirm', { name: selSrc }))) return;
    try {
      const r = await api('/api/sap-usage/data', { method: 'DELETE', body: JSON.stringify({ sourceFile: selSrc }) });
      setMsg({ text: r.message, type: 'success' }); setSelSrc(''); load(0);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  };

  const delAll = async () => {
    if (!window.confirm(t('sap.delAllConfirm'))) return;
    if (window.prompt('This removes ALL SAP Usage rows. Type DELETE ALL to continue.') !== 'DELETE ALL') return;
    try {
      const r = await api('/api/sap-usage/data', { method: 'DELETE', body: JSON.stringify({ all: true }) });
      setMsg({ text: r.message, type: 'success' }); load(0);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  };

  const openSapModal = async () => {
    setSapModal(true);
    try {
      const c = await api('/api/sap/connections');
      setSapConns(Array.isArray(c) ? c : []);
      if (Array.isArray(c) && c.length) { const def = c.find(x => isPrdConn(x) && String(x.landscape).toUpperCase() === 'ID') || c.find(isPrdConn) || c[0]; setSapForm(f => (f.connectionId ? f : { ...f, connectionId: String(def.id) })); }
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  };
  const runSapFetch = async () => {
    if (!sapForm.connectionId) { setMsg({ text: 'Pilih profil koneksi SAP dulu.', type: 'error' }); return; }
    if (!sapForm.dateFrom)     { setMsg({ text: 'Tanggal awal wajib diisi.', type: 'error' }); return; }
    setSapBusy(true);
    try {
      const r = await api('/api/sap-usage/fetch-sap', { method: 'POST', body: JSON.stringify({ ...sapForm, connectionId: Number(sapForm.connectionId) }) });
      setMsg({ text: r.message, type: 'success' });
      setSapModal(false); load(0); loadCov();
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
    finally { setSapBusy(false); }
  };

  const sources = stat.sources || [];
  const totalPages = Math.ceil(total / PAGE);

  const COLS = [
    { key: 'User',        label: 'User',         sticky: true },
    { key: 'Company',     label: 'Company' },
    { key: 'Period',      label: 'Period' },
    { key: 'Module',      label: 'Module' },
    { key: 'Dept',        label: 'Dept' },
    { key: 'Landscape',   label: 'Landscape' },
    { key: 'LicenseType', label: 'License Type' },
    { key: 'Lic',         label: 'Lic' },
    { key: 'TotalHours',  label: 'Total Hours',  numeric: true },
    { key: 'source_file', label: 'Source File' },
  ];

  const stickyTh = { background: '#f8fafc', padding: '9px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#64748b', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em', position: 'sticky', left: 0, zIndex: 2, boxShadow: '2px 0 4px rgba(0,0,0,0.06)' };
  const stickyTd = { padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#0f172a', fontWeight: 700, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: '#fff', zIndex: 1, boxShadow: '2px 0 4px rgba(0,0,0,0.04)' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label={t('sap.statTotal')} value={fmt(total || stat.count)} />
        <StatCard label={t('sap.statPeriods')} value={(stat.periods || []).length} />
        <StatCard label={t('tickets.statSources')} value={sources.length} />
        <StatCard label={t('tickets.statPage')} value={(page + 1) + ' / ' + (totalPages || 1)} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{t('tm.tab.sap')}</span>
          <Badge text={ti('users.rows', { n: fmt(total || stat.count) })} color="#10b981" />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={search} onChange={e => handleSearch(e.target.value)} placeholder={'🔍 ' + t('sap.searchPh')} style={{ ...inputStyle, width: 220 }} />
          <ExportXlsxButton
            onExport={async () => { const all = await fetchAllPaged(api, '/api/sap-usage/data', 'data', { search }); exportRowsToXlsx(all, COLS, 'sap_usage.xlsx', 'SAP Usage'); }}
            onError={e => setMsg({ text: e.message, type: 'error' })} />
          <Btn size="sm" variant="primary" onClick={openSapModal}>📡 Fetch SAP</Btn>
          <Btn size="sm" onClick={async () => {
            try { const r = await api('/api/sap-usage/backfill-license', { method: 'POST' }); setMsg({ text: r.message, type: 'success' }); load(0); }
            catch (e) { setMsg({ text: e.message, type: 'error' }); }
          }}>{t('tblMaint.isiLicense')}</Btn>
          <UploadCsvButton mode="json" endpoint="/api/sap-usage/save"
            jsonBuilder={(rawRows, name) => {
              function parseHMS(s) {
                const p = (s || '').trim().split(':');
                return p.length === 3 ? parseInt(p[0]) + parseInt(p[1]) / 60 + parseInt(p[2]) / 3600 : 0;
              }
              const firstRow = rawRows[0] || {};
              const keys = Object.keys(firstRow);
              const find = (...names) => keys.find(k => names.some(n => k.toLowerCase() === n.toLowerCase())) || null;
              const kDept = find('Dept.', 'Dept');
              const kUser = find('User');
              const kPeriod = find('Period');
              const kTotal = find('Total');
              const kLic = find('Lic.', 'Lic');
              const kLT = find('License Type', 'LicenseType');
              const kLandscape = find('Landscape');

              const rows = rawRows
                .filter(r => r[kDept] && r[kUser])
                .map(r => {
                  const dept = (r[kDept] || '').trim();
                  const user = (r[kUser] || '').trim();
                  const landscape = kLandscape && r[kLandscape] ? r[kLandscape].trim() : 'ID';
                  const di = dept.indexOf('-');
                  const company = landscape === 'KL' ? 'PIL' : (di > 0 ? dept.substring(0, di) : dept);
                  const module  = di > 0 ? dept.substring(di + 1) : dept;
                  const totalHours = parseFloat(parseHMS(r[kTotal]).toFixed(2));
                  return { Dept: dept, Period: kPeriod ? (r[kPeriod] || '') : '', User: user, Lic: kLic ? (r[kLic] || '') : '', LicenseType: kLT ? (r[kLT] || '') : '', Landscape: landscape, Company: company, Module: module, TotalHours: totalHours };
                });
              return { rows, sourceFile: name, mode: 'replace' };
            }}
            onDone={r => { setMsg({ text: r.msg, type: r.ok ? 'success' : 'error' }); if (r.ok) load(0); }} />
          <select value={selSrc} onChange={e => setSelSrc(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 180 }}>
            <option value="">{t('tickets.selectSource')}</option>
            {sources.map(s => <option key={s.source_file} value={s.source_file}>{s.source_file} ({ti('users.rows', { n: fmt(s.rows) })})</option>)}
          </select>
          <Btn size="sm" variant="danger" onClick={delSrc}>🗑️ {t('tickets.btnDelSource')}</Btn>
          <Btn size="sm" variant="warning" onClick={delAll}>⚠️ {t('tickets.btnClearAll')}</Btn>
        </div>
      </div>
      <Alert msg={msg?.text} type={msg?.type} onClose={() => setMsg(null)} />
      <CronScheduler api={api} jobKey="sap_usage_monthly"
        runConfirm="Jalankan fetch SAP untuk BULAN LALU sekarang? Data periode itu (landscape yang sama) akan diganti." />
      {sapCov.length > 0 && (() => {
        const months = sapRecentMonths(13);
        const lands = [...new Set(sapCov.map(r => r.landscape || 'ID'))];
        const colors = { complete: ['#dcfce7', '#166534'], partial: ['#fef9c3', '#854d0e'], unknown: ['#e0f2fe', '#075985'], missing: ['#f1f5f9', '#94a3b8'] };
        return (
          <div style={{ margin: '4px 0 10px', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>Data coverage — last 13 months (click a month to fetch it again from SAP)</div>
            {lands.map(l => (
              <div key={l} style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, minWidth: 44 }}>{l}</span>
                {months.map(p => {
                  const r = sapCov.find(x => (x.landscape || 'ID') === l && x.period === p);
                  const st = r ? r.status : 'missing';
                  const [bg, fg] = colors[st] || colors.unknown;
                  const tip = r ? `${p}: ${r.users} users, ${r.hours} h — ${st}${r.coveredTo ? ' (data until ' + r.coveredTo + ')' : ''}` : `${p}: no data`;
                  return (
                    <button key={p} type="button" title={tip} onClick={() => { openSapModal(); pickSapPeriod(p); }}
                      style={{ border: 'none', cursor: 'pointer', borderRadius: 6, padding: '3px 7px', fontSize: 11, fontWeight: 600, background: bg, color: fg }}>
                      {SAP_MONTHS[+p.slice(5) - 1]} {p.slice(2, 4)}{st === 'partial' ? ' ◐' : ''}
                    </button>
                  );
                })}
              </div>
            ))}
            <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>Green = full month · Yellow ◐ = partial month · Blue = uploaded file · Grey = no data</div>
          </div>
        );
      })()}
      {sapModal && (
        <Modal title={t('tblMaint.fetchUserUtilizationSapZrfc')} onClose={() => !sapBusy && setSapModal(false)}>
          <div style={{ display: 'grid', gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>Profil Koneksi (SAP Config)</label>
              <Select value={sapForm.connectionId} onChange={e => setSapForm(f => ({ ...f, connectionId: e.target.value }))}>
                <option value="">{t('tblMaint.pilihKoneksi')}</option>
                {[...sapConns].sort((a, b) => (isPrdConn(b) - isPrdConn(a)) || String(a.landscape).localeCompare(String(b.landscape))).map(c => <option key={c.id} value={c.id}>{isPrdConn(c) ? '' : '⚠ '}{c.landscape} · {c.environment || '-'} · {c.sid || c.host} / client {c.client || '-'}</option>)}
              </Select>
            </div>
            {(() => { const c = sapConns.find(x => String(x.id) === String(sapForm.connectionId)); if (!c || isPrdConn(c)) return null; return (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}>
                ⚠️ <b>{c.sid} client {c.client}</b> is a <b>{c.environment || 'non-PRD'}</b> system. User usage reports must come from PRD.
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, fontWeight: 600 }}>
                  <input type="checkbox" checked={!!sapForm.allowNonPrd} onChange={e => setSapForm(f => ({ ...f, allowNonPrd: e.target.checked }))} /> I know — fetch from this system anyway
                </label>
              </div>); })()}
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>Period</label>
              <Select value={sapPeriodPick} onChange={e => pickSapPeriod(e.target.value)}>
                <option value="">Custom date range</option>
                {sapPeriodOptions().map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </Select>
              <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 3 }}>Pick a month or trimester — the dates below are filled in automatically. Re-fetching a month replaces that month's existing data.</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>{t('tblMaint.tanggal')}</label>
                <Input type="date" value={sapForm.dateFrom} onChange={e => setSapForm(f => ({ ...f, dateFrom: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>{t('tblMaint.tanggalSampai')}</label>
                <Input type="date" value={sapForm.dateTo} onChange={e => setSapForm(f => ({ ...f, dateTo: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>User Pattern (UNAME)</label>
                <Input value={sapForm.uname} onChange={e => setSapForm(f => ({ ...f, uname: e.target.value }))} placeholder="PG*" />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>Department (opsional)</label>
                <Input value={sapForm.department} onChange={e => setSapForm(f => ({ ...f, department: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>SOAP Path</label>
                <Input value={sapForm.soapPath} onChange={e => setSapForm(f => ({ ...f, soapPath: e.target.value }))} placeholder="/sap/bc/soap/rfc" />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>{t('tblMaint.modeSimpan')}</label>
                <Select value={sapForm.mode} onChange={e => setSapForm(f => ({ ...f, mode: e.target.value }))}>
                  <option value="replace">Replace fetched months (recommended)</option>
                  <option value="append">Append / merge</option>
                </Select>
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              {t('tblMaint.dataDetailLoginDiagregasiOtomatis')}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <Btn onClick={() => setSapModal(false)} disabled={sapBusy}>{t('tblMaint.batal')}</Btn>
              <Btn variant="primary" onClick={runSapFetch} disabled={sapBusy}>{sapBusy ? '⏳ Mengambil…' : t('tblMaint.fetchSimpan')}</Btn>
            </div>
          </div>
        </Modal>
      )}
      {loading ? <Loading /> : (
        <>
          <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e2e8f0', maxHeight: 520, overflowY: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: 'max-content' }}>
              <thead>
                <tr>{COLS.map(c => c.sticky ? <th key={c.key} style={stickyTh}>{c.label}</th> : <Th key={c.key}>{c.label}</Th>)}</tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={COLS.length}><Empty icon="📊" text={t('sap.empty')} /></td></tr>}
                {rows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    {COLS.map(c => {
                      if (c.sticky) return <td key={c.key} style={stickyTd}>{row[c.key] || '—'}</td>;
                      if (c.numeric) return <Td key={c.key}><strong>{parseFloat(row[c.key] || 0).toFixed(2)}h</strong></Td>;
                      if (c.key === 'source_file') return <Td key={c.key}><Tag text={shortVal(row[c.key])} /></Td>;
                      if (c.key === 'Period') return <Td key={c.key}><Tag text={row[c.key]} /></Td>;
                      return <Td key={c.key} title={String(row[c.key] || '')}>{shortVal(row[c.key])}</Td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', justifyContent: 'center' }}>
              <Btn size="sm" disabled={page === 0} onClick={() => load(page - 1, search)}>← {t('tickets.prev')}</Btn>
              <span style={{ fontSize: 13, color: '#64748b' }}>{ti('sap.pager', { p: page + 1, tp: totalPages, a: fmt(rows.length), b: fmt(total) })}</span>
              <Btn size="sm" disabled={page >= totalPages - 1} onClick={() => load(page + 1, search)}>{t('tickets.next')} →</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ── Penjadwalan cron via UI (dipakai di tab AWS Billing & AWS Cost) ─────────────
// last_run ditulis dengan datetime('now') SQLite, yang SELALU UTC — sementara
// jadwal cron berjalan pada tz job (Asia/Jakarta). Tanpa konversi, '0 6 * * 1'
// terlihat berjalan pukul 23:00 hari sebelumnya, yang membingungkan.
//
// 'Z' WAJIB ditambahkan: string DB tidak punya penanda zona, dan tanpa 'Z'
// JavaScript memperlakukannya sebagai waktu LOKAL sehingga hasilnya salah 7 jam.


function BillingPanel({ api: rawApi }) {
  const api = useCallback((p, o) => rawApi(withAwsAcct(p), o), [rawApi]);
  const [stat, setStat]   = useState({});
  const { t } = useLang();
  const ti = (key, vars) => {
    let s = t(key);
    if (vars) for (const k in vars) s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), vars[k]);
    return s;
  };
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]     = useState(null);
  const [page, setPage]   = useState(0);
  const [search, setSearch] = useState('');
  const PAGE = 100;

  // Fetch langsung dari AWS Cost Explorer (pengganti upload CSV)
  const now = new Date();
  const [fy, setFy] = useState(now.getFullYear());
  const [fm, setFm] = useState(now.getMonth() === 0 ? 12 : now.getMonth()); // default: bulan lalu
  const [fetching, setFetching] = useState(false);

  const load = useCallback(async (p = 0, q = '') => {
    setLoading(true);
    try {
      const sq = q ? `&search=${encodeURIComponent(q)}` : '';
      const [s, d] = await Promise.all([
        api('/api/billing/status'),
        api(`/api/billing/data?limit=${PAGE}&offset=${p * PAGE}${sq}`),
      ]);
      setStat(s); setRows(d.rows || []); if (d.total !== undefined) setStat(prev => ({...prev, count: d.total})); setPage(p);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(0); }, [load]);

  const handleSearch = (val) => { setSearch(val); setPage(0); load(0, val); };

  const fetchAws = async () => {
    if (!window.confirm(ti('billing.fetchConfirm', { period: fy + '/' + String(fm).padStart(2, '0') }))) return;
    setFetching(true);
    setMsg(null);
    try {
      const r = await api('/api/billing/fetch-aws?account=' + (localStorage.getItem('awsinv.account') || 'primary') + '', {
        method: 'POST',
        body: JSON.stringify({ year: fy, month: fm, mode: 'replace' }),
      });
      const tt = r.totals || {};
      setMsg({
        text: ti('billing.fetchResult', { message: r.message, count: r.count, cost: (tt.costBeforeTax ?? 0).toLocaleString(), tax: (tt.tax ?? 0).toLocaleString(), total: (tt.total ?? 0).toLocaleString() }),
        type: 'success',
      });
      load(0);
    } catch (e) {
      setMsg({ text: e.message, type: 'error' });
    } finally {
      setFetching(false);
    }
  };

  const delAll = async () => {
    const acct = await confirmAwsClear(api, 'billing');
    if (!acct) return;
    try {
      const r = await api('/api/billing/data?account=' + encodeURIComponent(acct.key), { method: 'DELETE' });
      setMsg({ text: r.message, type: 'success' }); load(0);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  };

  const totalPages = Math.ceil((stat.count || 0) / PAGE);

  // Semua kolom sesuai tabel aws_billing_raw di database
  const COLS = [
    { key: 'id',                     label: 'ID',              sticky: true },
    { key: 'BillingPeriodStartDate', label: 'Period Start' },
    { key: 'BillingPeriodEndDate',   label: 'Period End' },
    { key: 'PayerAccountId',         label: 'Payer ID' },
    { key: 'PayerAccountName',       label: 'Payer Name' },
    { key: 'ProductName',            label: 'Product Name' },
    { key: 'ProductCode',            label: 'Product Code' },
    { key: 'UsageType',              label: 'Usage Type' },
    { key: 'UsageQuantity',          label: 'Usage Qty', qty: true },
    { key: 'UsageUnit',              label: 'Unit' },
    { key: 'ItemDescription',        label: 'Item Description' },
    { key: 'CurrencyCode',           label: 'Currency' },
    { key: 'CostBeforeTax',          label: 'Cost Before Tax', numeric: true },
    { key: 'TaxAmount',              label: 'Tax Amount',      numeric: true },
    { key: 'TotalCost',              label: 'Total Cost',      numeric: true },
    { key: 'Credits',                label: 'Credits',         numeric: true },
    { key: 'TaxType',                label: 'Tax Type' },
    { key: 'RecordType',             label: 'Record Type' },
    { key: 'SellerOfRecord',         label: 'Seller' },
    { key: 'created_at',             label: 'Created At' },
  ];

  const stickyTh = { background: '#f8fafc', padding: '9px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#64748b', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em', position: 'sticky', left: 0, zIndex: 2, boxShadow: '2px 0 4px rgba(0,0,0,0.06)' };
  const stickyTd = { padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#0f172a', fontWeight: 700, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: '#fff', zIndex: 1, boxShadow: '2px 0 4px rgba(0,0,0,0.04)' };

  const fmtUsd = (val) => '$' + parseFloat(val || 0).toFixed(4);

  return (
    <div>
      <AwsAccountBar api={api} />
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label={t('sap.statTotal')} value={fmt(stat.count)} />
        <StatCard label={t('billing.statPeriods')} value={(stat.periods || []).length} />
        <StatCard label={t('tickets.statPage')} value={(page + 1) + ' / ' + (totalPages || 1)} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{t('billing.titleRaw')}</span>
          <Badge text={ti('users.rows', { n: fmt(stat.count) })} color="#d97706" />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={search} onChange={e => handleSearch(e.target.value)} placeholder={'🔍 ' + t('billing.searchPh')} style={{ ...inputStyle, width: 220 }} />
          <ExportXlsxButton
            onExport={async () => { const all = await fetchAllPaged(api, '/api/billing/data', 'rows', { search, pageSize: 500 }); exportRowsToXlsx(all, COLS, 'aws_billing.xlsx', 'AWS Billing'); }}
            onError={e => setMsg({ text: e.message, type: 'error' })} />
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{t('billing.period')}</span>
          <select value={fm} onChange={e => setFm(parseInt(e.target.value, 10))} style={{ ...inputStyle, width: 130, background: '#fff' }}>
            {t('billing.monthsShort').split(',').map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
          <input type="number" value={fy} min={2015} max={2099} onChange={e => setFy(parseInt(e.target.value, 10) || now.getFullYear())} style={{ ...inputStyle, width: 90 }} />
          <Btn size="sm" variant="primary" disabled={fetching} onClick={fetchAws}>
            {fetching ? '⏳ ' + t('tickets.fetching') : '☁️ ' + t('billing.btnFetchAws')}
          </Btn>
          <UploadCsvButton mode="json" endpoint={withAwsAcct("/api/billing/save")}
            jsonBuilder={(rows) => {
              // 1. Filter header berulang (AWS billing CSV punya header setiap ~200 baris)
              // 2. Filter hanya RecordType === 'PayerLineItem' — sama dengan AWS Billing dashboard
              const filtered = rows.filter(r =>
                r.BillingPeriodStartDate !== 'BillingPeriodStartDate' &&
                r.PayerAccountId !== 'PayerAccountId' &&
                (r.RecordType || '').toString() === 'PayerLineItem'
              );
              // Replace per periode (YYYY-MM) — normalisasi separator (2026/05 & 2026-05 dianggap sama)
              const periods = [...new Set(filtered
                .map(r => String(r.BillingPeriodStartDate || '').slice(0, 7).replace(/\//g, '-'))
                .filter(p => /^\d{4}-\d{2}$/.test(p)))];
              return { rows: filtered, mode: 'replace', periods };
            }}
            onDone={r => { setMsg({ text: r.msg, type: r.ok ? 'success' : 'error' }); if (r.ok) load(0); }} />
          <Btn size="sm" variant="warning" onClick={delAll}>⚠️ {t('billing.btnClearTable')}</Btn>
        </div>
      </div>
      <Alert msg={msg?.text} type={msg?.type} onClose={() => setMsg(null)} />
      <CronScheduler api={api} jobKey="aws_billing" />
      {loading ? <Loading /> : (
        <>
          <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e2e8f0', maxHeight: 520, overflowY: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: 'max-content' }}>
              <thead>
                <tr>
                  {COLS.map(c => c.sticky
                    ? <th key={c.key} style={stickyTh}>{c.label}</th>
                    : <Th key={c.key}>{c.label}</Th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={COLS.length}><Empty icon="💳" text={t('billing.empty')} /></td></tr>
                )}
                {rows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    {COLS.map(c => {
                      if (c.sticky) return <td key={c.key} style={stickyTd}>{row[c.key] ?? '—'}</td>;
                      if (c.qty) return <Td key={c.key}>{row[c.key] != null ? Number(row[c.key]).toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—'}</Td>;
                      if (c.numeric) {
                        const numVal = parseFloat(row[c.key] || 0);
                        return (
                          <Td key={c.key}>
                            <strong style={{ color: numVal > 0 ? '#0f172a' : '#94a3b8' }}>{fmtUsd(row[c.key])}</strong>
                          </Td>
                        );
                      }
                      return <Td key={c.key} title={String(row[c.key] || '')}>{shortVal(row[c.key])}</Td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', justifyContent: 'center' }}>
              <Btn size="sm" disabled={page === 0} onClick={() => load(page - 1)}>← {t('tickets.prev')}</Btn>
              <span style={{ fontSize: 13, color: '#64748b' }}>
                {ti('sap.pager', { p: page + 1, tp: totalPages, a: fmt(rows.length), b: fmt(stat.count) })}
              </span>
              <Btn size="sm" disabled={page >= totalPages - 1} onClick={() => load(page + 1)}>{t('tickets.next')} →</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AwsPanel({ api: rawApi }) {
  const api = useCallback((p, o) => rawApi(withAwsAcct(p), o), [rawApi]);
  const [years, setYears]     = useState([]);
  const { t } = useLang();
  const ti = (key, vars) => {
    let s = t(key);
    if (vars) for (const k in vars) s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), () => String(vars[k]));
    return s;
  };
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(0);
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]         = useState(null);
  const PAGE = 100;

  // Fetch langsung dari AWS Cost Explorer — mode range (from bulan/tahun → to bulan/tahun)
  const now = new Date();
  const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const prevYear  = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const [fromY, setFromY] = useState(prevYear);
  const [fromM, setFromM] = useState(prevMonth);
  const [toY,   setToY]   = useState(prevYear);
  const [toM,   setToM]   = useState(prevMonth);
  const [fetching, setFetching]     = useState(false);
  const [fetchProgress, setFetchProgress] = useState('');

  const load = useCallback(async (p = 0, q = '') => {
    setLoading(true);
    try {
      const sq = q ? `&search=${encodeURIComponent(q)}` : '';
      const [y, d] = await Promise.all([
        api('/api/aws/years'),
        api(`/api/aws/cost-raw?limit=${PAGE}&offset=${p * PAGE}${sq}`),
      ]);
      setYears(y.years || []);
      setRows(d.rows || []);
      setTotal(d.total || 0);
      setPage(p);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(0); }, [load]);

  const handleSearch = (val) => { setSearch(val); setPage(0); load(0, val); };

  const delAll = async () => {
    const acct = await confirmAwsClear(api, 'cost');
    if (!acct) return;
    try {
      const r = await api('/api/aws/cost-raw?account=' + encodeURIComponent(acct.key), { method: 'DELETE' });
      setMsg({ text: r.message || t('aws.cleared'), type: 'success' });
      load(0);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  };

  const fetchAws = async () => {
    // Pastikan semua nilai bertipe number (bukan string dari state)
    const fY = parseInt(fromY, 10), fM = parseInt(fromM, 10);
    const tY = parseInt(toY,   10), tM = parseInt(toM,   10);
    if (!fY || !fM || !tY || !tM) { setMsg({ text: t('aws.invalidPeriod'), type: 'error' }); return; }
    // Susun daftar bulan dalam rentang from→to
    const months = [];
    let cy = fY, cm = fM;
    const endKey = tY * 100 + tM;
    while (cy * 100 + cm <= endKey) {
      months.push({ year: cy, month: cm });
      if (cm === 12) { cy++; cm = 1; } else cm++;
      if (months.length > 60) break; // batas wajar: maks 5 tahun
    }
    if (!months.length) { setMsg({ text: t('aws.invalidRange'), type: 'error' }); return; }

    // Guard: Cost Explorer hanya menyimpan histori ±37 bulan ke belakang dari bulan ini.
    // Bulan yang lebih tua tidak bisa di-fetch (pakai upload CSV) → di-skip otomatis.
    const minDate = new Date(now.getFullYear(), now.getMonth(), 1);
    minDate.setMonth(minDate.getMonth() - 37);
    const minKey = (minDate.getFullYear()) * 100 + (minDate.getMonth() + 1);
    const skipped = months.filter(m => m.year * 100 + m.month < minKey);
    const valid   = months.filter(m => m.year * 100 + m.month >= minKey);
    if (!valid.length) {
      setMsg({ text: t('aws.allOutOfRange'), type: 'error' });
      return;
    }
    const skipNote = skipped.length
      ? ti('aws.skipNote', { n: skipped.length, list: skipped.map(m => `${m.year}/${String(m.month).padStart(2,'0')}`).join(', ') })
      : '';
    const label = valid.length === 1
      ? `${valid[0].year}/${String(valid[0].month).padStart(2,'0')}`
      : ti('aws.rangeLabel', { a: `${valid[0].year}/${String(valid[0].month).padStart(2,'0')}`, b: `${valid[valid.length-1].year}/${String(valid[valid.length-1].month).padStart(2,'0')}`, n: valid.length });
    if (!window.confirm(ti('aws.fetchConfirm', { label, skip: skipNote ? '\n' + skipNote : '' }))) return;
    setFetching(true);
    setMsg(null);
    const results = [];
    for (let i = 0; i < valid.length; i++) {
      const { year, month } = valid[i];
      const periodLabel = `${year}/${String(month).padStart(2,'0')}`;
      setFetchProgress(ti('aws.progress', { period: periodLabel, i: i + 1, n: valid.length }));
      try {
        const r = await api('/api/aws/fetch-aws?account=' + (localStorage.getItem('awsinv.account') || 'primary') + '', {
          method: 'POST',
          body: JSON.stringify({ year, month, mode: 'replace' }),
        });
        results.push({ period: periodLabel, ok: true, count: r.count, total: r.totals?.total ?? 0 });
      } catch (e) {
        results.push({ period: periodLabel, ok: false, error: e.message });
      }
    }
    setFetchProgress('');
    setFetching(false);
    const failed  = results.filter(r => !r.ok);
    const success = results.filter(r => r.ok);
    const grandTotal = success.reduce((s, r) => s + (r.total || 0), 0);
    const summary = success.map(r => ti('aws.monthRows', { period: r.period, count: r.count, total: (r.total||0).toFixed(2) })).join(' | ');
    if (failed.length === 0) {
      setMsg({ text: ti('aws.successAll', { n: valid.length, summary, grand: grandTotal.toFixed(2), skip: skipNote }), type: skipped.length ? 'info' : 'success' });
    } else {
      const errDetail = failed.map(r => `${r.period}: ${r.error}`).join('; ');
      setMsg({ text: ti('aws.partial', { ok: success.length, fail: failed.length, summary: summary || '—', err: errDetail, skip: skipNote }), type: 'error' });
    }
    load(0);
  };

  const totalPages = Math.ceil(total / PAGE);

  const COLS = [
    { key: 'id',            label: 'ID',            sticky: true },
    { key: 'upload_id',     label: 'Upload ID' },
    { key: 'usage_type',    label: 'Usage Type' },
    { key: 'product_code',  label: 'Product Code' },
    { key: 'year',          label: 'Year' },
    { key: 'month_num',     label: 'Month' },
    { key: 'month_name',    label: 'Month Name' },
    { key: 'business_unit', label: 'Business Unit' },
    { key: 'usd',           label: 'USD',           numeric: true },
  ];

  const stickyTh = { background: '#f8fafc', padding: '9px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#64748b', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em', position: 'sticky', left: 0, zIndex: 2, boxShadow: '2px 0 4px rgba(0,0,0,0.06)' };
  const stickyTd = { padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#0f172a', fontWeight: 700, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: '#fff', zIndex: 1, boxShadow: '2px 0 4px rgba(0,0,0,0.04)' };

  return (
    <div>
      <AwsAccountBar api={api} />
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label={t('aws.statYears')} value={years.join(', ') || '—'} />
        <StatCard label={t('sap.statTotal')} value={fmt(total)} />
        <StatCard label={t('tickets.statPage')} value={(page + 1) + ' / ' + (totalPages || 1)} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{t('aws.titleRaw')}</span>
          <Badge text={ti('users.rows', { n: fmt(total) })} color="#f97316" />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={search} onChange={e => handleSearch(e.target.value)} placeholder={'🔍 ' + t('aws.searchPh')} style={{ ...inputStyle, width: 220 }} />
          <ExportXlsxButton
            onExport={async () => { const all = await fetchAllPaged(api, '/api/aws/cost-raw', 'rows', { search }); exportRowsToXlsx(all, COLS, 'aws_cost_raw.xlsx', 'AWS Cost'); }}
            onError={e => setMsg({ text: e.message, type: 'error' })} />
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{t('aws.from')}</span>
          <select value={fromM} onChange={e => setFromM(parseInt(e.target.value, 10))} style={{ ...inputStyle, width: 110, background: '#fff' }}>
            {t('billing.monthsShort').split(',').map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
          <input type="number" value={fromY} min={2015} max={2099} onChange={e => setFromY(parseInt(e.target.value, 10) || prevYear)} style={{ ...inputStyle, width: 80 }} />
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{t('aws.to')}</span>
          <select value={toM} onChange={e => setToM(parseInt(e.target.value, 10))} style={{ ...inputStyle, width: 110, background: '#fff' }}>
            {t('billing.monthsShort').split(',').map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
          <input type="number" value={toY} min={2015} max={2099} onChange={e => setToY(parseInt(e.target.value, 10) || prevYear)} style={{ ...inputStyle, width: 80 }} />
          {fetching && fetchProgress && (
            <span style={{ fontSize: 12, color: 'rgb(var(--brand-600))', fontWeight: 500 }}>{fetchProgress}</span>
          )}
          <Btn size="sm" variant="primary" disabled={fetching} onClick={fetchAws}>
            {fetching ? '⏳ ' + t('tickets.fetching') : '☁️ ' + t('billing.btnFetchAws')}
          </Btn>
          <UploadCsvButton mode="multipart" endpoint={withAwsAcct("/api/aws/upload")}
            onDone={r => { setMsg({ text: r.msg, type: r.ok ? 'success' : 'error' }); if (r.ok) load(0); }} />
          <Btn size="sm" variant="warning" onClick={delAll}>⚠️ {t('tickets.btnClearAll')}</Btn>
        </div>
      </div>
      <Alert msg={msg?.text} type={msg?.type} onClose={() => setMsg(null)} />
      <CronScheduler api={api} jobKey="aws_cost" />

      {/* Raw data table */}
      {loading ? <Loading /> : (
        <>
          <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e2e8f0', maxHeight: 520, overflowY: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: 'max-content' }}>
              <thead>
                <tr>
                  {COLS.map(c => c.sticky
                    ? <th key={c.key} style={stickyTh}>{c.label}</th>
                    : <Th key={c.key}>{c.label}</Th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={COLS.length}><Empty icon="☁️" text={t('aws.empty')} /></td></tr>
                )}
                {rows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    {COLS.map(c => {
                      if (c.sticky) return <td key={c.key} style={stickyTd}>{row[c.key] ?? '—'}</td>;
                      if (c.numeric) {
                        const val = parseFloat(row[c.key] || 0);
                        return <Td key={c.key}><strong style={{ color: val > 0 ? '#0f172a' : '#94a3b8' }}>${val.toFixed(4)}</strong></Td>;
                      }
                      if (c.key === 'usage_type' || c.key === 'product_code') {
                        // Auto-fit: teks penuh tanpa potong & tanpa maxWidth → kolom melebar sesuai isi (tabel tetap bisa scroll horizontal)
                        return <td key={c.key} style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#1e293b', whiteSpace: 'nowrap' }}>{row[c.key] ?? '—'}</td>;
                      }
                      return <Td key={c.key} title={String(row[c.key] || '')}>{shortVal(row[c.key])}</Td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', justifyContent: 'center' }}>
              <Btn size="sm" disabled={page === 0} onClick={() => load(page - 1)}>← {t('tickets.prev')}</Btn>
              <span style={{ fontSize: 13, color: '#64748b' }}>
                {ti('sap.pager', { p: page + 1, tp: totalPages, a: fmt(rows.length), b: fmt(total) })}
              </span>
              <Btn size="sm" disabled={page >= totalPages - 1} onClick={() => load(page + 1)}>{t('tickets.next')} →</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
}


function IdSuimPanel({ api }) {
  const {
    t
  } = useLang();

  const [stat, setStat]     = useState({});
  const [rows, setRows]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [selSrc, setSelSrc] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]       = useState(null);
  const [page, setPage]     = useState(0);
  const [search, setSearch] = useState('');
  const PAGE = 100;

  const load = useCallback(async (p = 0, q = '') => {
    setLoading(true);
    try {
      const sq = q ? `&search=${encodeURIComponent(q)}` : '';
      const [s, d] = await Promise.all([
        api('/api/id-suim/status'),
        api(`/api/id-suim/data?limit=${PAGE}&offset=${p * PAGE}${sq}`),
      ]);
      setStat(s); setRows(d.data || []); setTotal(d.total || 0); setPage(p);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(0); }, [load]);

  const handleSearch = (val) => { setSearch(val); setPage(0); load(0, val); };

  const delSrc = async () => {
    if (!selSrc) { setMsg({ text: 'Pilih source file dulu.', type: 'error' }); return; }
    if (!window.confirm(t('tblMaint.hapusIdSuimData', {
      selSrc
    }))) return;
    try {
      const r = await api('/api/id-suim/data', { method: 'DELETE', body: JSON.stringify({ sourceFile: selSrc }) });
      setMsg({ text: r.message, type: 'success' }); setSelSrc(''); load(0);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  };

  const delAll = async () => {
    if (!window.confirm(t('tblMaint.kosongkanSemuaDataIdSuim'))) return;
    try {
      const r = await api('/api/id-suim/data', { method: 'DELETE', body: JSON.stringify({}) });
      setMsg({ text: r.message, type: 'success' }); load(0);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  };

  const sources = stat.sources || [];
  const totalPages = Math.ceil(total / PAGE);

  const COLS = [
    { key: 'UserName',     label: 'User Name',     sticky: true },
    { key: 'CompleteName', label: 'Complete Name' },
    { key: 'UserType',     label: 'User Type' },
    { key: 'Department',   label: 'Department' },
    { key: 'Company',      label: 'Company' },
    { key: 'Module',       label: 'Module' },
    { key: 'EmailAddress', label: 'E-Mail Address' },
    { key: 'ValidFrom',    label: 'Valid From' },
    { key: 'ValidThrough', label: 'Valid Through' },
    { key: 'source_file',  label: 'Source File' },
  ];

  const stickyTh = { background: '#f8fafc', padding: '9px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#64748b', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em', position: 'sticky', left: 0, zIndex: 2, boxShadow: '2px 0 4px rgba(0,0,0,0.06)' };
  const stickyTd = { padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#0f172a', fontWeight: 700, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: '#fff', zIndex: 1, boxShadow: '2px 0 4px rgba(0,0,0,0.04)' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label={t('tblMaint.totalUsers')} value={fmt(total || stat.count)} />
        <StatCard label="Source Files" value={sources.length} />
        <StatCard label={t('tblMaint.halaman')} value={(page + 1) + ' / ' + (totalPages || 1)} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>ID SUIM</span>
          <Badge text={fmt(total || stat.count) + ' rows'} color="#10b981" />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={search} onChange={e => handleSearch(e.target.value)} placeholder={t('tblMaint.cariUserNamaDeptEmail')} style={{ ...inputStyle, width: 220 }} />
          <ExportXlsxButton
            onExport={async () => { const all = await fetchAllPaged(api, '/api/id-suim/data', 'data', { search }); exportRowsToXlsx(all, COLS, 'id_suim.xlsx', 'ID SUIM'); }}
            onError={e => setMsg({ text: e.message, type: 'error' })} />
          <UploadCsvButton mode="json" endpoint="/api/id-suim/save"
            jsonBuilder={(rawRows, name) => {
              const firstRow = rawRows[0] || {};
              const keys = Object.keys(firstRow);
              const norm = k => String(k).replace(/\uFEFF/g, '').trim().toLowerCase();
              const find = (...names) => keys.find(k => names.some(n => norm(k) === n.toLowerCase())) || null;
              const kUser  = find('User name', 'User');
              const kName  = find('Complete name', 'Complete Name');
              const kFrom  = find('Valid From', 'Valid from');
              const kThru  = find('Valid through', 'Valid Through');
              const kType  = find('User Type', 'User type');
              const kDept  = find('Department');
              const kMail  = find('E-Mail Address', 'E-mail Address', 'Email Address');
              const g = (r, k) => (k && r[k] != null ? String(r[k]).trim() : '');
              const rows = rawRows
                .filter(r => g(r, kUser))
                .map(r => ({
                  UserName:     g(r, kUser),
                  CompleteName: g(r, kName),
                  ValidFrom:    g(r, kFrom),
                  ValidThrough: g(r, kThru),
                  UserType:     g(r, kType),
                  Department:   g(r, kDept),
                  EmailAddress: g(r, kMail),
                }));
              return { rows, sourceFile: name };
            }}
            onDone={r => { setMsg({ text: r.msg, type: r.ok ? 'success' : 'error' }); if (r.ok) load(0); }} />
          <select value={selSrc} onChange={e => setSelSrc(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 180 }}>
            <option value="">{t('tblMaint.pilihSourceFile')}</option>
            {sources.map(s => <option key={s.source_file} value={s.source_file}>{s.source_file} ({fmt(s.rows)} rows)</option>)}
          </select>
          <Btn size="sm" variant="danger" onClick={delSrc}>{t('tblMaint.hapusSource')}</Btn>
          <Btn size="sm" variant="warning" onClick={delAll}>{t('tblMaint.kosongkanSemua')}</Btn>
        </div>
      </div>
      <Alert msg={msg?.text} type={msg?.type} onClose={() => setMsg(null)} />
      {loading ? <Loading /> : (
        <>
          <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e2e8f0', maxHeight: 520, overflowY: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: 'max-content' }}>
              <thead>
                <tr>{COLS.map(c => c.sticky ? <th key={c.key} style={stickyTh}>{c.label}</th> : <Th key={c.key}>{c.label}</Th>)}</tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={COLS.length}><Empty icon="🔑" text={t('tblMaint.belumAdaDataIdSuim')} /></td></tr>}
                {rows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    {COLS.map(c => {
                      if (c.sticky) return <td key={c.key} style={stickyTd}>{row[c.key] || '—'}</td>;
                      if (c.key === 'source_file') return <Td key={c.key}><Tag text={shortVal(row[c.key])} /></Td>;
                      return <Td key={c.key} title={String(row[c.key] || '')}>{shortVal(row[c.key])}</Td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', justifyContent: 'center' }}>
              <Btn size="sm" disabled={page === 0} onClick={() => load(page - 1, search)}>← Prev</Btn>
              <span style={{ fontSize: 13, color: '#64748b' }}>{t('tblMaint.pageOfRecords', { page: page + 1, totalPages, shown: fmt(rows.length), total: fmt(total) })}</span>
              <Btn size="sm" disabled={page >= totalPages - 1} onClick={() => load(page + 1, search)}>Next →</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
}


function UploadHistoryPanel({ api }) {
  const {
    t
  } = useLang();

  const [awsUploads, setAwsUploads] = useState([]);
  const [ticketSrc, setTicketSrc]   = useState([]);
  const [sapSrc, setSapSrc]         = useState([]);
  const [suimSrc, setSuimSrc]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [msg, setMsg]               = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aws, tk, sap, suim] = await Promise.all([
        api('/api/aws/uploads').catch(() => ({})),
        api('/api/tickets/sources').catch(() => ({})),
        api('/api/sap-usage/status').catch(() => ({})),
        api('/api/id-suim/status').catch(() => ({})),
      ]);
      setAwsUploads(aws.uploads || []);
      setTicketSrc(tk.sources || []);
      setSapSrc(sap.sources || []);
      setSuimSrc(suim.sources || []);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const delAwsUpload = async (id, filename) => {
    if (!window.confirm(t('tblMaint.hapusUploadBesertaSemuaDatanya', {
      filename
    }))) return;
    try {
      await api('/api/aws/uploads/' + id, { method: 'DELETE' });
      setMsg({ text: `Upload "${filename}" dihapus.`, type: 'success' }); load();
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  };

  const SrcTable = ({ title, color, items }) => (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{title}</span>
        <Badge text={(items?.length || 0) + ' source file'} color={color} />
      </div>
      <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><Th>Source File</Th><Th>Rows</Th></tr></thead>
          <tbody>
            {(!items || items.length === 0) && <tr><td colSpan={2}><Empty icon="📄" text={t('tblMaint.belumAdaSourceFile')} /></td></tr>}
            {(items || []).map((s, i) => (
              <tr key={i}><Td><strong>{s.source_file}</strong></Td><Td>{fmt(s.rows)}</Td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Upload History</span>
          <Badge text={t('tblMaint.riwayatUploadSourceFileSemua')} color="#64748b" />
        </div>
        <Btn size="sm" onClick={load}>🔄 Refresh</Btn>
      </div>
      <Alert msg={msg?.text} type={msg?.type} onClose={() => setMsg(null)} />
      {loading ? <Loading /> : (
        <>
          {/* AWS Cost — upload files (dapat dihapus) */}
          <div style={{ marginBottom: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>AWS Cost — Upload Files</span>
              <Badge text={awsUploads.length + ' upload'} color="#f97316" />
            </div>
            <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><Th>ID</Th><Th>Filename</Th><Th>Rows</Th><Th>Uploaded By</Th><Th>Waktu Upload</Th><Th>Aksi</Th></tr></thead>
                <tbody>
                  {awsUploads.length === 0 && <tr><td colSpan={6}><Empty icon="☁️" text={t('tblMaint.belumAdaUpload')} /></td></tr>}
                  {awsUploads.map(r => (
                    <tr key={r.id}>
                      <Td>{r.id}</Td><Td><strong>{r.filename}</strong></Td>
                      <Td>{fmt(r.row_count)}</Td><Td>{r.uploaded_by}</Td>
                      <Td>{shortVal(r.uploaded_at)}</Td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}>
                        <Btn size="sm" variant="danger" onClick={() => delAwsUpload(r.id, r.filename)}>{t('tblMaint.hapus')}</Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <SrcTable title="Solman Tickets — Source Files" color="rgb(var(--brand-600))" items={ticketSrc} />
          <SrcTable title="SAP Usage — Source Files"      color="#10b981" items={sapSrc} />
          <SrcTable title="ID SUIM — Source Files"         color="#0ea5e9" items={suimSrc} />

          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
            {t('tblMaint.penghapusanSourceFileSolmanSap')}
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab Config ─────────────────────────────────────────────────────────────────

// ── SQL Editor (test query ke DB SQLite dashboard) — admin-only ─────────────────
const SQL_TEMPLATES = [
  { label: 'SELECT preview',       sql: 'SELECT * FROM {table} LIMIT 100;' },
  { label: 'COUNT rows',           sql: 'SELECT COUNT(*) AS total FROM {table};' },
  { label: 'Latest rows',          sql: 'SELECT * FROM {table} ORDER BY rowid DESC LIMIT 50;' },
  { label: 'Column info (PRAGMA)', sql: 'PRAGMA table_info({table});' },
  { label: 'Table schema (DDL)',   sql: "SELECT sql FROM sqlite_master WHERE type='table' AND name='{table}';" },
  { label: 'List all tables',      sql: "SELECT name FROM sqlite_master WHERE type='table' ORDER BY name;" },
  { label: 'DB page info',         sql: 'PRAGMA page_count; PRAGMA freelist_count; PRAGMA page_size;' },
  { label: '── Write ──',          sql: null },
  { label: 'UPDATE template',      sql: 'UPDATE {table} SET column = value WHERE id = ?;' },
  { label: 'DELETE by id',         sql: 'DELETE FROM {table} WHERE id = ?;' },
  { label: 'INSERT template',      sql: 'INSERT INTO {table} (col1, col2) VALUES (val1, val2);' },
];

function SqlEditorPanel({ api }) {
  const [sql, setSql]               = useState("SELECT name FROM sqlite_master WHERE type='table' ORDER BY name;");
  const [allowWrite, setAllowWrite] = useState(false);
  const [busy, setBusy]             = useState(false);
  const [alert, setAlert]           = useState(null);
  const [result, setResult]         = useState(null);
  const [tables, setTables]         = useState([]);
  const [target, setTarget]         = useState('dashboard'); // 'dashboard' | 'aiservice'
  const [lastTable, setLastTable]   = useState('');
  const { t } = useLang();
  const ti = (key, vars) => {
    let s = t(key);
    if (vars) for (const k in vars) s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), () => String(vars[k]));
    return s;
  };

  const tablesUrl = target === 'aiservice' ? '/api/ai-sql/tables' : '/api/sql/tables';
  const execUrl   = target === 'aiservice' ? '/api/ai-sql/execute' : '/api/sql/execute';

  const loadTables = useCallback(() => {
    api(tablesUrl).then(t => setTables(t || [])).catch(() => setTables([]));
  }, [api, tablesUrl]);

  useEffect(() => {
    setResult(null);
    setAlert(null);
    setTables([]);
    setLastTable('');
    loadTables();
  }, [loadTables]);

  const run = async () => {
    if (!sql.trim()) return;
    setBusy(true); setAlert(null); setResult(null);
    try {
      const d = await api(execUrl, { method: 'POST', body: JSON.stringify({ sql, allowWrite }) });
      setResult(d);
      if (d.type === 'write') {
        setAlert({ type: 'success', msg: ti('sql.writeOk', { stmt: d.statement, n: fmt(d.rowsModified ?? 0) }) });
        loadTables();
      } else {
        setAlert({ type: 'success', msg: ti('sql.readMsg', { n: fmt(d.rowCount), trunc: d.truncated ? t('sql.truncated') : '' }) });
      }
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(false); }
  };

  const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); run(); } };
  const pickTable = (name) => {
    setLastTable(name);
    setSql(`SELECT * FROM ${name} LIMIT 100;`);
  };
  const applyTemplate = (e) => {
    const tpl = e.target.value;
    e.target.value = '';
    if (!tpl) return;
    setSql(tpl.replace(/\{table\}/g, lastTable || 'tabel'));
  };
  const cols = result?.type === 'read' ? (result.columns || []).map(c => ({ key: c, label: c })) : [];

  return (
    <div>
      <Alert msg={alert?.msg} type={alert?.type} onClose={() => setAlert(null)} />
      {/* Target selector */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[
          { key: 'dashboard', label: '🗄️ Dashboard DB' },
          { key: 'aiservice', label: '🤖 AI Service DB' },
        ].map(opt => (
          <button key={opt.key} onClick={() => setTarget(opt.key)}
            style={{
              padding: '5px 14px', fontSize: 12, fontFamily: 'inherit', fontWeight: 600,
              borderRadius: 20, cursor: 'pointer', transition: 'all 0.15s',
              border: target === opt.key ? '1.5px solid rgb(var(--brand-600))' : '1.5px solid #e2e8f0',
              background: target === opt.key ? 'rgb(var(--brand-50))' : '#f8fafc',
              color: target === opt.key ? 'rgb(var(--brand-600))' : '#64748b',
            }}>
            {opt.label}
          </button>
        ))}
      </div>
      {tables.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{t('sql.tablesLabel')}</span>
          {tables.map(tbl => (
            <button key={tbl.name} onClick={() => pickTable(tbl.name)} title={`SELECT * FROM ${tbl.name}`}
              style={{
                fontSize: 11, padding: '3px 8px', borderRadius: 6, cursor: 'pointer', transition: 'all 0.1s',
                border: lastTable === tbl.name ? '1px solid rgb(var(--brand-600))' : '1px solid #e2e8f0',
                background: lastTable === tbl.name ? 'rgb(var(--brand-50))' : '#f8fafc',
                color: lastTable === tbl.name ? 'rgb(var(--brand-600))' : '#475569',
                fontWeight: lastTable === tbl.name ? 600 : 400,
              }}>
              {tbl.name}{tbl.rows != null ? ` (${fmt(tbl.rows)})` : ''}
            </button>
          ))}
        </div>
      )}
      {/* Template selector */}
      <div style={{ marginBottom: 8 }}>
        <select defaultValue="" onChange={applyTemplate}
          style={{ ...inputStyle, fontSize: 12, color: '#475569', background: '#f8fafc', width: 'auto', minWidth: 220 }}>
          <option value="" disabled>📋 Template query{lastTable ? ` → ${lastTable}` : ''}</option>
          {SQL_TEMPLATES.map((tpl, i) =>
            tpl.sql === null
              ? <option key={i} disabled>{tpl.label}</option>
              : <option key={i} value={tpl.sql}>{tpl.label}</option>
          )}
        </select>
        {lastTable && (
          <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8' }}>
            {t('tblMaint.tabelAktif')} <b style={{ color: 'rgb(var(--brand-600))' }}>{lastTable}</b>
          </span>
        )}
      </div>
      <textarea value={sql} onChange={e => setSql(e.target.value)} onKeyDown={onKey} rows={8} spellCheck={false}
        placeholder="SELECT * FROM users;"
        style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 13, resize: 'vertical', minHeight: 140, lineHeight: 1.5 }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: allowWrite ? '#dc2626' : '#64748b', cursor: 'pointer', fontWeight: allowWrite ? 700 : 500 }}>
          <input type="checkbox" checked={allowWrite} onChange={e => setAllowWrite(e.target.checked)} />
          {t('sql.allowWrite')}
        </label>
        <Btn variant="primary" disabled={busy} onClick={run}>{busy ? '⏳ ' + t('sql.running') : '▶️ ' + t('sql.btnRun')}</Btn>
      </div>
      {allowWrite && (
        <div style={{ fontSize: 12, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px', marginTop: 8 }}>
          {t('sql.warnA')}<b>{t('sql.warnBold')}</b>{t('sql.warnB')}
        </div>
      )}
      <div style={{ marginTop: 16 }}>
        {result?.type === 'read' && (
          result.rows.length === 0
            ? <Empty icon="📭" text={t('sql.empty0')} />
            : (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                  <ExportXlsxButton
                    onExport={async () => exportRowsToXlsx(result.rows, cols, 'query_result.xlsx', 'Result')}
                    onError={(e) => setAlert({ type: 'error', msg: t('sql.exportFail') + (e.message || e) })} />
                </div>
                <ResultTable columns={cols} rows={result.rows} />
              </>
            )
        )}
      </div>
    </div>
  );
}

// ── SAP Auth Guard: kredensial yang diblokir dashboard setelah 401 dari SAP ─────
// Backend: sapAuthGuard.js (GET/DELETE /api/sap/auth-blocks). Blok lepas sendiri
// bila password profil diganti; tombol Reset hanya untuk kasus user SAP sempat
// terkunci (password benar) lalu sudah di-unlock di SU01.
function SapAuthBlocksPanel({ api }) {
  const [data, setData]   = useState(null);
  const [alert, setAlert] = useState(null);
  const [busyKey, setBusyKey] = useState('');

  const load = useCallback(async () => {
    try { setData(await api('/api/sap/auth-blocks')); }
    catch (e) { setAlert({ type: 'error', msg: e.message }); setData({ blocks: [], protected: [] }); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const reset = async (b) => {
    if (!window.confirm(
      `Reset blok untuk ${b.user}@${b.host} client ${b.client || '-'}?\n\n` +
      'Lakukan HANYA bila password di profil sudah benar dan user sudah di-unlock di SAP (SU01). ' +
      'Panggilan berikutnya akan dikirim lagi ke SAP; bila password masih salah, SAP mencatat 1 logon gagal dan kredensial diblokir lagi.'
    )) return;
    setBusyKey(b.key);
    try {
      await api(`/api/sap/auth-blocks/${encodeURIComponent(b.key)}`, { method: 'DELETE' });
      setAlert({ type: 'success', msg: 'Blok dihapus.' });
      await load();
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusyKey(''); }
  };

  const fmtTs = (s) => { if (!s) return '—'; const d = new Date(s); return isNaN(d) ? s : d.toLocaleString('id-ID', { hour12: false }); };
  const td = { padding: '6px 10px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' };
  const blocks = data?.blocks || [];
  const prot   = data?.protected || [];

  return (
    <div style={{ marginTop: 20, border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>🛡️ SAP Auth Guard — kredensial diblokir</div>
          <div style={{ fontSize: 11, color: '#64748b' }}>
            Bila SAP membalas 401, kredensial (host + client + user + password) diblokir di dashboard agar user SAP tidak terkunci.
            Blok lepas otomatis saat password di profil diganti.
          </div>
        </div>
        <Btn size="sm" onClick={load}>🔄</Btn>
      </div>
      <Alert msg={alert?.msg} type={alert?.type} onClose={() => setAlert(null)} />
      {data === null ? <Loading /> : blocks.length === 0 ? (
        <div style={{ fontSize: 12, color: '#16a34a', padding: '6px 2px' }}>✅ Tidak ada kredensial yang diblokir.</div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #fecaca', borderRadius: 8 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#fef2f2' }}>
                {['Host:Port', 'Client', 'User', 'Endpoint', 'Diblokir sejak', '401 terakhir', 'Jumlah 401', ''].map((h, i) => (
                  <th key={i} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #fecaca', whiteSpace: 'nowrap', color: '#7f1d1d', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {blocks.map((b) => (
                <tr key={b.key}>
                  <td style={td}>{b.host}:{b.port}</td>
                  <td style={td}>{b.client || '—'}</td>
                  <td style={td}><b>{b.user}</b></td>
                  <td style={{ ...td, color: '#64748b' }} title={b.path}>{b.path}</td>
                  <td style={td}>{fmtTs(b.blockedAt)}</td>
                  <td style={td}>{fmtTs(b.lastAt)}</td>
                  <td style={td}>{b.count}</td>
                  <td style={td}>
                    <Btn size="sm" variant="danger" disabled={busyKey === b.key} onClick={() => reset(b)}>
                      {busyKey === b.key ? '⏳' : '♻️ Reset'}
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {prot.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 11, color: '#64748b' }}>
          🔒 User terlindung (tidak boleh dipakai dashboard, env <code>SAP_PROTECTED_USERS</code>):{' '}
          {prot.map((p, i) => <span key={i} style={{ marginRight: 10 }}><b>{p.user}</b> @ {p.host} / {p.client}</span>)}
        </div>
      )}
    </div>
  );
}

// ── SAP Config (profil koneksi per Landscape/Environment, CRUD → SQLite) ────────
function SapConfigPanel({ api }) {
  const [rows, setRows]       = useState(null);
  const { t } = useLang();
  const ti = (key, vars) => {
    let s = t(key);
    if (vars) for (const k in vars) s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), () => String(vars[k]));
    return s;
  };
  const [alert, setAlert]     = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy]       = useState(false);

  const load = useCallback(async () => {
    try { setRows(await api('/api/sap/connections')); }
    catch (e) { setAlert({ type: 'error', msg: e.message }); setRows([]); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const blank = { id: null, landscape: 'ID', environment: 'DEV', sid: '', host: '', sn: '', client: '', username: '', password: '', pathUserlist: '', pathLock: '', pathUnlock: '', pathChange: '', pathReadtable: '', pathUserdetail: '', pathActgroups: '', pathAuthmod: '', pathRoledownload: '', pathReadsource: '', pathSysinfo: '', pathServerlist: '', pathWpinfo: '' };
  const td = { padding: '6px 10px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' };
  const LBL = { sid: 'SID', host: 'Host / IP', sn: 'System No. (SN)', client: 'Client', username: 'User' };

  const save = async () => {
    const e = editing;
    if (!e.landscape) { setAlert({ type: 'error', msg: t('sapcfg.errLandscape') }); return; }
    setBusy(true);
    try {
      if (e.id) await api(`/api/sap/connections/${e.id}`, { method: 'PUT',  body: JSON.stringify(e) });
      else      await api('/api/sap/connections',        { method: 'POST', body: JSON.stringify(e) });
      setEditing(null);
      setAlert({ type: 'success', msg: t('sapcfg.saved') });
      await load();
    } catch (err) { setAlert({ type: 'error', msg: err.message }); }
    finally { setBusy(false); }
  };

  const del = async (r) => {
    if (!window.confirm(ti('sapcfg.delConfirm', { name: r.landscape + '/' + (r.environment || '-') }))) return;
    try { await api(`/api/sap/connections/${r.id}`, { method: 'DELETE' }); setAlert({ type: 'success', msg: t('sapcfg.deleted') }); await load(); }
    catch (e) { setAlert({ type: 'error', msg: e.message }); }
  };

  if (rows === null) return <Loading />;

  return (
    <div>
      <Alert msg={alert?.msg} type={alert?.type} onClose={() => setAlert(null)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12 }}>
        <div style={{ fontSize: 12, color: '#64748b' }}>{t('sapcfg.intro')}</div>
        <Btn variant="primary" onClick={() => setEditing({ ...blank })}>➕ {t('sapcfg.btnAdd')}</Btn>
      </div>

      {rows.length === 0 ? <Empty icon="🗄️" text={t('sapcfg.empty')} /> : (
        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                {['Landscape', 'Env', 'SID', 'Host', 'SN', 'Client', 'User', 'Password', t('sapcfg.colUpdated'), ''].map((h, i) => (
                  <th key={i} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', color: '#334155', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} style={{ background: i % 2 ? '#fff' : '#fafafa' }}>
                  <td style={td}><b>{r.landscape}</b></td>
                  <td style={td}>{r.environment || '—'}</td>
                  <td style={td}>{r.sid || '—'}</td>
                  <td style={td}>{r.host || '—'}</td>
                  <td style={td}>{r.sn || '—'}</td>
                  <td style={td}>{r.client || '—'}</td>
                  <td style={td}>{r.username || '—'}</td>
                  <td style={td}>{r.hasPassword ? '••••••' : '—'}</td>
                  <td style={{ ...td, color: '#94a3b8' }}>{r.updatedAt || '—'}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn size="sm" onClick={() => setEditing({ id: r.id, landscape: r.landscape, environment: r.environment, sid: r.sid, host: r.host, sn: r.sn, client: r.client, username: r.username, password: '', pathUserlist: r.pathUserlist || '', pathLock: r.pathLock || '', pathUnlock: r.pathUnlock || '', pathChange: r.pathChange || '', pathReadtable: r.pathReadtable || '', pathUserdetail: r.pathUserdetail || '', pathActgroups: r.pathActgroups || '', pathAuthmod: r.pathAuthmod || '', pathRoledownload: r.pathRoledownload || '', pathReadsource: r.pathReadsource || '', pathSysinfo: r.pathSysinfo || '', pathServerlist: r.pathServerlist || '', pathWpinfo: r.pathWpinfo || '' })}>✏️</Btn>
                      <Btn size="sm" variant="danger" onClick={() => del(r)}>🗑️</Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SapAuthBlocksPanel api={api} />

      {editing && (
        <Modal title={editing.id ? t('sapcfg.modalEdit') : t('sapcfg.modalAdd')} onClose={() => setEditing(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Landscape">
              <Select value={editing.landscape} onChange={e => setEditing(s => ({ ...s, landscape: e.target.value }))}>
                <option>ID</option><option>JB</option><option>KL</option><option>Solman</option>
              </Select>
            </Field>
            <Field label="Environment">
              <Select value={editing.environment} onChange={e => setEditing(s => ({ ...s, environment: e.target.value }))}>
                <option value="DEV">DEV</option><option value="QAS">QAS</option><option value="PRD">PRD</option><option value="">— (none)</option>
              </Select>
            </Field>
          </div>
          {['sid', 'host', 'sn', 'client', 'username'].map(k => (
            <Field key={k} label={LBL[k]}>
              <Input value={editing[k]} onChange={e => setEditing(s => ({ ...s, [k]: e.target.value }))} />
            </Field>
          ))}
          <Field label={editing.id ? t('sapcfg.fPwEdit') : t('sapcfg.fPw')}>
            <Input type="password" value={editing.password} onChange={e => setEditing(s => ({ ...s, password: e.target.value }))} />
          </Field>
          <div style={{ marginTop: 6, marginBottom: 8, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{t('sapcfg.bapiTitle')}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>{t('sapcfg.bapiHint')}</div>
          </div>
          {[['pathUserlist', 'BAPI_USER_GETLIST (list)'], ['pathLock', 'BAPI_USER_LOCK'], ['pathUnlock', 'BAPI_USER_UNLOCK'], ['pathChange', 'BAPI_USER_CHANGE (reset pw)'], ['pathReadtable', 'RFC_READ_TABLE (detail user)'], ['pathUserdetail', 'BAPI_USER_GET_DETAIL (role user)'], ['pathActgroups', 'BAPI_USER_ACTGROUPS_ASSIGN (assign role)'], ['pathAuthmod', 'Z_ROLE_AUTH_MODIFY (ubah authorization)'], ['pathRoledownload', 'Z_ROLE_DOWNLOAD (download .SAP)'], ['pathReadsource', 'ZRFC_READ_SOURCE (baca source ABAP)'], ['pathSysinfo', 'RFC_SYSTEM_INFO (system info)'], ['pathServerlist', 'TH_SERVER_LIST (server list)'], ['pathWpinfo', 'TH_WPINFO (work processes)']].map(([k, lbl]) => (
            <Field key={k} label={lbl}>
              <Input value={editing[k] || ''} onChange={e => setEditing(s => ({ ...s, [k]: e.target.value }))} placeholder="/sap/bc/srt/rfc/sap/..." />
            </Field>
          ))}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn onClick={() => setEditing(null)}>{t('common.cancel')}</Btn>
            <Btn variant="primary" disabled={busy} onClick={save}>{busy ? '⏳ ' + t('sapcfg.saving') : '💾 ' + t('common.save')}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── SAP RFC Test (Opsi B: RFC_READ_TABLE via SOAP/SOAMANAGER) ───────────────────
// Read-only proof-of-concept. Koneksi changeable; kredensial TIDAK disimpan
// (dikirim per-request ke backend, tidak ke SQLite). Target: ECC6 EHP7.
function ResultTable({ columns, rows }) {
  const { t } = useLang();
  const ti = (key, vars) => {
    let s = t(key);
    if (vars) for (const k in vars) s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), () => String(vars[k]));
    return s;
  };
  return (
    <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#f1f5f9' }}>
            {columns.map(c => (
              <th key={c.key} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', color: '#334155', fontWeight: 600 }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ background: i % 2 ? '#fff' : '#fafafa' }}>
              {columns.map(c => (
                <td key={c.key} title={r[c.key]} style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{shortVal(r[c.key])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ padding: '6px 10px', fontSize: 11, color: '#94a3b8' }}>{ti('users.rows', { n: fmt(rows.length) })}</div>
    </div>
  );
}

// ── HR: Employee Overtime ─────────────────────────────────────────────────────

function OvertimePanel({ api }) {
  const {
    t
  } = useLang();

  const [stat, setStat]       = useState({});
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]         = useState(null);
  const [page, setPage]       = useState(0);
  const [search, setSearch]   = useState('');
  const [selSrc, setSelSrc]   = useState('');
  const [filterYear,  setFilterYear]  = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterBu,    setFilterBu]    = useState('');
  const PAGE = 100;

  const buildQs = (p, q, y, m, bu) => {
    const params = [`limit=${PAGE}`, `offset=${p * PAGE}`];
    if (q)  params.push(`search=${encodeURIComponent(q)}`);
    if (y)  params.push(`year=${y}`);
    if (m)  params.push(`month=${m}`);
    if (bu) params.push(`bu=${encodeURIComponent(bu)}`);
    return '/api/overtime/data?' + params.join('&');
  };

  const load = useCallback(async (p = 0, q = '', y = '', m = '', bu = '') => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        api('/api/overtime/status'),
        api(buildQs(p, q, y, m, bu)),
      ]);
      setStat(s); setRows(d.data || []); setTotal(d.total || 0); setPage(p);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(0); }, [load]);

  const applyFilters = (p = 0) => load(p, search, filterYear, filterMonth, filterBu);

  const delSrc = async () => {
    if (!selSrc) { setMsg({ text: 'Pilih source file dulu.', type: 'error' }); return; }
    if (!window.confirm(t('tblMaint.hapusOvertimeData', {
      selSrc
    }))) return;
    try {
      const r = await api('/api/overtime/data', { method: 'DELETE', body: JSON.stringify({ sourceFile: selSrc }) });
      setMsg({ text: r.message, type: 'success' }); setSelSrc(''); load(0);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  };

  const delAll = async () => {
    if (!window.confirm(t('tblMaint.kosongkanSemuaDataEmployeeOvertime'))) return;
    try {
      const r = await api('/api/overtime/data', { method: 'DELETE', body: JSON.stringify({}) });
      setMsg({ text: r.message, type: 'success' }); load(0);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  };

  const sources    = stat.sources    || [];
  const periods    = stat.periods    || [];
  const bus        = stat.business_units || [];
  const totalPages = Math.ceil(total / PAGE);

  const MONTHS = ['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agt','Sep','Okt','Nov','Des'];

  const COLS = [
    { key: 'employee_code', label: 'Kode',        sticky: true },
    { key: 'employee_name', label: 'Nama',         sticky: false },
    { key: 'business_unit', label: 'BU' },
    { key: 'department',    label: 'Dept' },
    { key: 'section',       label: 'Section' },
    { key: 'year',          label: 'Tahun' },
    { key: 'month',         label: 'Bulan' },
    { key: 'duty_date',     label: 'Tgl Tugas' },
    { key: 'ot_start',      label: 'Mulai' },
    { key: 'ot_end',        label: 'Selesai' },
    { key: 'ot_hours',      label: 'Jam OT',   numeric: true },
    { key: 'total_ot_hours',label: 'Total Jam', numeric: true },
    { key: 'ot_amount',     label: 'Amount',    money: true },
    { key: 'reason',        label: 'Reason' },
    { key: 'source_file',   label: 'Source' },
  ];

  const stickyTh = { background: '#f8fafc', padding: '9px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#64748b', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em', position: 'sticky', left: 0, zIndex: 2, boxShadow: '2px 0 4px rgba(0,0,0,0.06)' };
  const stickyTd = { padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#0f172a', fontWeight: 700, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: '#fff', zIndex: 1, boxShadow: '2px 0 4px rgba(0,0,0,0.04)' };

  return (
    <div>
      {/* Stat Cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label={t('tblMaint.totalRecords')} value={fmt(total || stat.count)} />
        <StatCard label="Periode"       value={periods.length} />
        <StatCard label="Business Unit" value={bus.length} />
        <StatCard label="Source Files"  value={sources.length} />
        <StatCard label={t('tblMaint.halaman')}       value={(page + 1) + ' / ' + (totalPages || 1)} />
      </div>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>⏱️ Employee Overtime</span>
          <Badge text={fmt(total || stat.count) + ' rows'} color="#f59e0b" />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Filters */}
          <select value={filterYear} onChange={e => setFilterYear(e.target.value)} style={{ ...inputStyle, width: 90 }}>
            <option value="">{t('tblMaint.semuaTahun')}</option>
            {[...new Set(periods.map(p => p.year))].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={{ ...inputStyle, width: 90 }}>
            <option value="">{t('tblMaint.semuaBln')}</option>
            {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select value={filterBu} onChange={e => setFilterBu(e.target.value)} style={{ ...inputStyle, width: 120 }}>
            <option value="">{t('tblMaint.semuaBu')}</option>
            {bus.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <Btn size="sm" onClick={() => applyFilters(0)}>🔍 Filter</Btn>
          <Btn size="sm" variant="secondary" onClick={() => { setFilterYear(''); setFilterMonth(''); setFilterBu(''); setSearch(''); load(0); }}>↺ Reset</Btn>

          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && applyFilters(0)}
            placeholder={t('tblMaint.cariNamaKodeDept')}
            style={{ ...inputStyle, width: 200 }} />

          <ExportXlsxButton
            onExport={async () => {
              const all = await fetchAllPaged(api, '/api/overtime/data', 'data', { pageSize: 5000 });
              exportRowsToXlsx(all, COLS, 'employee_overtime.xlsx', 'Overtime');
            }}
            onError={e => setMsg({ text: e.message, type: 'error' })} />

          <UploadCsvButton mode="json" endpoint="/api/overtime/save"
            label="📤 Upload CSV"
            jsonBuilder={(rawRows, name) => {
              const clean = rawRows.filter(r =>
                (r['Employee Code'] || r.employee_code) &&
                (r['Employee Name'] || r.employee_name)
              );
              return { rows: clean, sourceFile: name, mode: 'replace' };
            }}
            onDone={r => { setMsg({ text: r.msg, type: r.ok ? 'success' : 'error' }); if (r.ok) load(0); }} />

          <select value={selSrc} onChange={e => setSelSrc(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 180 }}>
            <option value="">{t('tblMaint.pilihSourceFile')}</option>
            {sources.map(s => <option key={s.source_file} value={s.source_file}>{s.source_file} ({fmt(s.rows)} rows)</option>)}
          </select>
          <Btn size="sm" variant="danger"   onClick={delSrc}>{t('tblMaint.hapusSource')}</Btn>
          <Btn size="sm" variant="warning"  onClick={delAll}>{t('tblMaint.kosongkanSemua')}</Btn>
        </div>
      </div>
      <Alert msg={msg?.text} type={msg?.type} onClose={() => setMsg(null)} />
      {loading ? <Loading /> : (
        <>
          <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e2e8f0', maxHeight: 520, overflowY: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: 'max-content' }}>
              <thead>
                <tr>
                  {COLS.map(c => c.sticky
                    ? <th key={c.key} style={stickyTh}>{c.label}</th>
                    : <Th key={c.key}>{c.label}</Th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={COLS.length}><Empty icon="⏱️" text={t('tblMaint.belumAdaDataOvertimeUpload')} /></td></tr>
                )}
                {rows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    {COLS.map(c => {
                      if (c.sticky)  return <td key={c.key} style={stickyTd}>{row[c.key] || '—'}</td>;
                      if (c.money)   return <Td key={c.key}><strong>{Number(row[c.key] || 0).toLocaleString('id-ID')}</strong></Td>;
                      if (c.numeric) return <Td key={c.key}>{parseFloat(row[c.key] || 0).toFixed(1)}</Td>;
                      if (c.key === 'source_file') return <Td key={c.key}><Tag text={shortVal(row[c.key])} /></Td>;
                      if (c.key === 'month') return <Td key={c.key}><Tag text={MONTHS[(row[c.key] || 1) - 1] || row[c.key]} /></Td>;
                      return <Td key={c.key} title={String(row[c.key] || '')}>{shortVal(row[c.key])}</Td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', justifyContent: 'center' }}>
              <Btn size="sm" disabled={page === 0} onClick={() => applyFilters(page - 1)}>◀ Prev</Btn>
              <span style={{ fontSize: 13, color: '#64748b' }}>Hal {page + 1} / {totalPages}</span>
              <Btn size="sm" disabled={page >= totalPages - 1} onClick={() => applyFilters(page + 1)}>Next ▶</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Sybase DR Monitor — admin-only ───────────────────────────────────────────
// Tab "Sybase DR": CRUD profil SSH + findings viewer + test SSH + cron scheduler

const SYBASE_DEFAULT_KEYWORDS = [
  'error','fatal','corrupt','deadlock','stack trace',
  'infected','cannot open','severity: 1','severity: 2',
];

function SybaseDrPanel({ api }) {
  const { t } = useLang();
  // Helper interpolasi: ti('sdr.x', { n: 3 }) → ganti {n} di string hasil t()
  const ti = (key, vars) => {
    let s = t(key);
    if (vars) for (const k in vars) s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), vars[k]);
    return s;
  };
  const [profiles,  setProfiles]  = useState([]);
  const [findings,  setFindings]  = useState([]);
  const [keyfiles,  setKeyfiles]  = useState([]);
  const [state,     setState]     = useState([]);
  const [busy,      setBusy]      = useState(false);
  const [msg,       setMsg]       = useState(null);
  const [modal,     setModal]     = useState(null);   // null | 'add' | 'edit'
  const [editing,   setEditing]   = useState(null);   // profile obj saat edit
  const [filterPid, setFilterPid] = useState('');     // filter findings per profile
  const [detailCtx, setDetailCtx] = useState(null);   // finding yg dibuka detail

  const emptyForm = { label:'', host:'', port:22, ssh_user:'', key_file:'', log_path:'', keywords: SYBASE_DEFAULT_KEYWORDS, active: true };

  const loadAll = useCallback(async () => {
    try {
      const [p, f, k, s] = await Promise.all([
        api('/api/sybase-dr/profiles'),
        api('/api/sybase-dr/findings?limit=100'),
        api('/api/sybase-dr/keyfiles'),
        api('/api/sybase-dr/state'),
      ]);
      setProfiles(p || []);
      setFindings(f || []);
      setKeyfiles(k || []);
      setState(s || []);
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  }, [api]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const showMsg = (text, type = 'success') => { setMsg({ text, type }); };

  // ── Save profil (add/edit) ─────────────────────────────────────────────────
  const saveProfile = async (form) => {
    setBusy(true); setMsg(null);
    try {
      const body = { ...form, keywords: form.keywords };
      if (editing) {
        await api(`/api/sybase-dr/profiles/${editing.id}`, { method: 'PUT', body: JSON.stringify(body) });
        showMsg(t('sdr.profileSaved'));
      } else {
        await api('/api/sybase-dr/profiles', { method: 'POST', body: JSON.stringify(body) });
        showMsg(t('sdr.profileAdded'));
      }
      setModal(null); setEditing(null);
      loadAll();
    } catch (e) { showMsg(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const deleteProfile = async (p) => {
    if (!window.confirm(ti('sdr.deleteConfirm', { name: p.label || p.host }))) return;
    setBusy(true);
    try {
      await api(`/api/sybase-dr/profiles/${p.id}`, { method: 'DELETE', body: JSON.stringify({}) });
      showMsg(t('sdr.profileDeleted')); loadAll();
    } catch (e) { showMsg(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const forceScan = async () => {
    if (!window.confirm(t('sdr.forceConfirm'))) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api('/api/sybase-dr/run?force=1', { method: 'POST', body: JSON.stringify({ force: true }) });
      showMsg(ti('sdr.forceDone', { n: r.alerts, p: r.profiles }), r.alerts ? 'success' : 'info');
      loadAll();
    } catch (e) { showMsg(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const testSsh = async (p) => {
    setBusy(true); setMsg(null);
    try {
      const r = await api(`/api/sybase-dr/test-ssh/${p.id}`, { method: 'POST', body: JSON.stringify({}) });
      showMsg(r.ok ? ti('sdr.sshOk', { res: r.response }) : `❌ ${r.error}`, r.ok ? 'success' : 'error');
    } catch (e) { showMsg(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const testEmail = async (p) => {
    setBusy(true); setMsg(null);
    try {
      const r = await api(`/api/sybase-dr/test-email/${p.id}`, { method: 'POST', body: JSON.stringify({}) });
      showMsg(r.ok ? ti('sdr.emailSent', { to: p.email_to }) : `❌ ${r.error}`, r.ok ? 'success' : 'error');
    } catch (e) { showMsg(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const resetState = async (p) => {
    if (!window.confirm(ti('sdr.resetConfirm', { name: p.label || p.host }))) return;
    try {
      await api(`/api/sybase-dr/state/${p.id}`, { method: 'DELETE', body: JSON.stringify({}) });
      showMsg(t('sdr.resetDone')); loadAll();
    } catch (e) { showMsg(e.message, 'error'); }
  };

  const clearFindings = async (p) => {
    if (!window.confirm(ti('sdr.clearConfirm', { name: p.label || p.host }))) return;
    try {
      await api(`/api/sybase-dr/findings/${p.id}`, { method: 'DELETE', body: JSON.stringify({}) });
      showMsg(t('sdr.clearDone')); loadAll();
    } catch (e) { showMsg(e.message, 'error'); }
  };

  // ── Findings filter ────────────────────────────────────────────────────────
  const shownFindings = filterPid
    ? findings.filter(f => String(f.profile_id) === String(filterPid))
    : findings;

  const getState = (profileId) =>
    state.find(s => String(s.profile_id) === String(profileId));

  return (
    <div>
      <AseHealthPanel api={api} CronScheduler={CronScheduler} />
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
        <span style={{ fontSize:15, fontWeight:700, color:'#0f172a' }}>🖥️ {t('sdr.title')}</span>
        <Badge text={ti('sdr.profilesActive', { n: profiles.filter(p=>p.active).length })} color="rgb(var(--brand-600))" />
        <Badge text={ti('sdr.findingsCount', { n: findings.length })} color={findings.length ? '#dc2626' : '#94a3b8'} />
      </div>
      <p style={{ fontSize:12, color:'#64748b', marginTop:0, marginBottom:16 }}>
        {t('sdr.desc')}
      </p>

      {msg && <Alert msg={msg.text} type={msg.type} onClose={() => setMsg(null)} />}

      {/* Cron scheduler */}
      <CronScheduler api={api} jobKey="sybase_dr_alert" runConfirm={t('sdr.runConfirm')} />

      <div style={{ marginTop:8, marginBottom:4 }}>
        <Btn variant="warning" size="sm" onClick={forceScan} disabled={busy}>
          {t('sdr.forceScan')}
        </Btn>
      </div>

      {/* ── Profil SSH ────────────────────────────────────────────────────── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, marginTop:20 }}>
        <span style={{ fontWeight:700, fontSize:13, color:'#0f172a' }}>{t('sdr.profilesSection')}</span>
        <Btn variant="primary" size="sm" onClick={() => { setEditing(null); setModal('add'); }}>
          {t('sdr.addProfile')}
        </Btn>
      </div>

      {profiles.length === 0 ? (
        <Empty icon="🔌" text={t('sdr.emptyProfiles')} />
      ) : (
        <div style={{ overflowX:'auto', marginBottom:24 }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr>
                <Th>{t('sdr.colLabel')}</Th><Th>{t('sdr.colHost')}</Th><Th>{t('sdr.colPort')}</Th><Th>{t('sdr.colUser')}</Th>
                <Th>{t('sdr.colKeyFile')}</Th><Th>{t('sdr.colLogPath')}</Th><Th>{t('sdr.colKeywords')}</Th>
                <Th>{t('sdr.colStatus')}</Th><Th>{t('sdr.colEmail')}</Th><Th>{t('sdr.colLastTs')}</Th><Th>{t('sdr.colActions')}</Th>
              </tr>
            </thead>
            <tbody>
              {profiles.map(p => {
                const st = getState(p.id);
                return (
                  <tr key={p.id}>
                    <Td>{p.label || '—'}</Td>
                    <Td title={p.host}>{p.host}</Td>
                    <Td>{p.port}</Td>
                    <Td>{p.ssh_user}</Td>
                    <Td title={p.key_file}>{p.key_file}</Td>
                    <Td title={p.log_path}>{p.log_path}</Td>
                    <Td title={(p.keywords||[]).join(', ')}>{ti('sdr.kwCount', { n: (p.keywords||[]).length })}</Td>
                    <Td>
                      <Badge
                        text={p.active ? t('sdr.active') : t('sdr.inactive')}
                        color={p.active ? '#10b981' : '#94a3b8'}
                      />
                    </Td>
                    <Td title={p.email_enabled ? p.email_to : t('sdr.emailOffTitle')}>
                      <Badge
                        text={p.email_enabled ? t('sdr.emailOn') : t('sdr.emailOff')}
                        color={p.email_enabled ? '#10b981' : '#94a3b8'}
                      />
                    </Td>
                    <Td title={st ? st.last_ts : t('sdr.tsNone')}>
                      {st ? (st.last_ts ? st.last_ts.slice(0,8)+'…' : t('sdr.baselineNone')) : '—'}
                    </Td>
                    <td style={{ padding:'6px 8px', borderBottom:'1px solid #f1f5f9', whiteSpace:'nowrap' }}>
                      <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                        <Btn size="sm" onClick={() => testSsh(p)} disabled={busy}>{t('sdr.testSsh')}</Btn>
                        {p.email_enabled && p.email_to && (
                          <Btn size="sm" onClick={() => testEmail(p)} disabled={busy}>{t('sdr.testEmail')}</Btn>
                        )}
                        <Btn size="sm" onClick={() => { setEditing(p); setModal('edit'); }}>✏️</Btn>
                        <Btn size="sm" variant="warning" onClick={() => resetState(p)}>{t('sdr.reset')}</Btn>
                        <Btn size="sm" variant="warning" onClick={() => clearFindings(p)}>{t('sdr.clearFindings')}</Btn>
                        <Btn size="sm" variant="danger" onClick={() => deleteProfile(p)} disabled={busy}>✕</Btn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Findings ──────────────────────────────────────────────────────── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
        <span style={{ fontWeight:700, fontSize:13, color:'#0f172a' }}>{t('sdr.findingsSection')}</span>
        <select
          value={filterPid}
          onChange={e => setFilterPid(e.target.value)}
          style={{ fontSize:12, padding:'4px 8px', border:'1px solid #e2e8f0', borderRadius:6 }}
        >
          <option value="">{t('sdr.allProfiles')}</option>
          {profiles.map(p => (
            <option key={p.id} value={p.id}>{p.label || p.host}</option>
          ))}
        </select>
      </div>

      {shownFindings.length === 0 ? (
        <Empty icon="✅" text={t('sdr.noFindings')} />
      ) : (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr><Th>{t('sdr.colTime')}</Th><Th>{t('sdr.colProfile')}</Th><Th>{t('sdr.colAnomaly')}</Th><Th>{t('sdr.colAlert')}</Th><Th>{t('sdr.colDetail')}</Th></tr>
            </thead>
            <tbody>
              {shownFindings.map(f => (
                <tr key={f.id}>
                  <Td>{f.found_at ? f.found_at.slice(0,19) : '—'}</Td>
                  <Td>{f.label || f.profile_id}</Td>
                  <Td title={f.anomaly_line} style={{ maxWidth:300, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'monospace', fontSize:11 }}>
                    {f.anomaly_line}
                  </Td>
                  <Td>
                    <Badge text={f.alerted ? t('sdr.alertSent') : t('sdr.alertPending')} color={f.alerted ? '#10b981' : '#f59e0b'} />
                  </Td>
                  <td style={{ padding:'6px 8px', borderBottom:'1px solid #f1f5f9' }}>
                    <Btn size="sm" onClick={() => setDetailCtx(f)}>{t('sdr.contextBtn')}</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modal: Add/Edit Profil ─────────────────────────────────────────── */}
      {modal && (
        <ProfileModal
          title={modal === 'add' ? t('sdr.modalAdd') : t('sdr.modalEdit')}
          initial={editing || emptyForm}
          keyfiles={keyfiles}
          busy={busy}
          onSave={saveProfile}
          onClose={() => { setModal(null); setEditing(null); }}
        />
      )}

      {/* ── Modal: Context lines ───────────────────────────────────────────── */}
      {detailCtx && (
        <Modal title={ti('sdr.contextTitle', { time: detailCtx.found_at ? detailCtx.found_at.slice(0,19) : '' })} onClose={() => setDetailCtx(null)}>
          <div style={{ fontSize:11, marginBottom:8, color:'#64748b' }}>
            {t('sdr.contextProfile')}: <strong>{detailCtx.label || detailCtx.profile_id}</strong>
          </div>
          <div style={{ fontSize:11, marginBottom:8, color:'#dc2626', fontFamily:'monospace', wordBreak:'break-all' }}>
            ⚠ {detailCtx.anomaly_line}
          </div>
          <pre style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:6,
            padding:'10px 12px', fontSize:11, fontFamily:'monospace', whiteSpace:'pre-wrap',
            wordBreak:'break-all', maxHeight:360, overflowY:'auto', color:'#1e293b', margin:0 }}>
            {detailCtx.context_lines || t('sdr.contextNone')}
          </pre>
        </Modal>
      )}
    </div>
  );
}

// ── Modal form profil SSH ─────────────────────────────────────────────────────
function ProfileModal({ title, initial, keyfiles, busy, onSave, onClose }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    label:    initial.label    || '',
    host:     initial.host     || '',
    port:     initial.port     || 22,
    ssh_user: initial.ssh_user || '',
    key_file: initial.key_file || '',
    log_path: initial.log_path || '',
    keywords: Array.isArray(initial.keywords) ? initial.keywords : SYBASE_DEFAULT_KEYWORDS,
    active:   initial.active !== undefined ? !!initial.active : true,
    email_enabled: !!initial.email_enabled,
    email_to: initial.email_to || '',
  });
  const [kwText, setKwText] = useState((form.keywords).join('\n'));

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    const keywords = kwText.split('\n').map(k => k.trim()).filter(Boolean);
    onSave({ ...form, keywords });
  };

  return (
    <Modal title={title} onClose={onClose}>
      <Field label={t('sdr.fLabel')}>
        <Input value={form.label} onChange={e => set('label', e.target.value)} placeholder="mis. ASE-DR-JB" />
      </Field>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 80px', gap:8 }}>
        <Field label="Host / IP">
          <Input value={form.host} onChange={e => set('host', e.target.value)} placeholder="192.168.1.10" />
        </Field>
        <Field label={t('sdr.fPort')}>
          <Input type="number" value={form.port} onChange={e => set('port', parseInt(e.target.value,10)||22)} />
        </Field>
      </div>
      <Field label={t('sdr.fUser')}>
        <Input value={form.ssh_user} onChange={e => set('ssh_user', e.target.value)} placeholder="sybase" />
      </Field>
      <Field label={t('sdr.fKeyFile')}>
        {keyfiles.length > 0 ? (
          <select
            value={form.key_file}
            onChange={e => set('key_file', e.target.value)}
            style={{ width:'100%', padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13, background:'#fff' }}
          >
            <option value="">{t('sdr.fKeyFilePick')}</option>
            {keyfiles.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        ) : (
          <Input value={form.key_file} onChange={e => set('key_file', e.target.value)} placeholder="id_rsa" />
        )}
        <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>{t('sdr.fKeyFileHint')}</div>
      </Field>
      <Field label={t('sdr.fLogPath')}>
        <Input value={form.log_path} onChange={e => set('log_path', e.target.value)} placeholder="/opt/sybase/ASE-16_0/install/server.log" />
      </Field>
      <Field label={t('sdr.fKeywords')}>
        <textarea
          value={kwText}
          onChange={e => setKwText(e.target.value)}
          rows={5}
          style={{ width:'100%', padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:8,
            fontSize:12, fontFamily:'monospace', boxSizing:'border-box', resize:'vertical' }}
        />
      </Field>
      <Field label={t('sdr.fStatus')}>
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
          <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} />
          {t('sdr.fActiveLabel')}
        </label>
      </Field>
      <Field label={t('sdr.fEmailAlert')}>
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer', marginBottom:8 }}>
          <input type="checkbox" checked={form.email_enabled} onChange={e => set('email_enabled', e.target.checked)} />
          {t('sdr.fEmailEnable')}
        </label>
        <Input
          value={form.email_to}
          onChange={e => set('email_to', e.target.value)}
          placeholder={t('sdr.fEmailTo')}
          disabled={!form.email_enabled}
        />
        <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>
          {t('sdr.fEmailHint')}
        </div>
      </Field>
      <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
        <Btn onClick={onClose}>{t('sdr.cancel')}</Btn>
        <Btn variant="primary" onClick={handleSave} disabled={busy || !form.host || !form.ssh_user || !form.key_file || !form.log_path}>
          {busy ? t('sdr.saving') : t('sdr.save')}
        </Btn>
      </div>
    </Modal>
  );
}

// ── ASE Errorlog Alert (scan + Telegram/Email + jadwal) — admin-only ──────────
function AseAlertPanel({ api }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState(null);
  const [pass, setPass] = useState('');   // smtp_pass baru (kosong = pakai yang tersimpan)
  const { t } = useLang();
  const ti = (key, vars) => {
    let s = t(key);
    if (vars) for (const k in vars) s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), vars[k]);
    return s;
  };

  const load = useCallback(async () => {
    try {
      const d = await api('/api/sap/ase-alert/config');
      setForm({
        keywords: Array.isArray(d.keywords) ? d.keywords : [],
        telegram_enabled: !!d.telegram_enabled,
        email_enabled: !!d.email_enabled,
        email_to: d.email_to || '',
        smtp_host: d.smtp_host || '', smtp_port: d.smtp_port || 587, smtp_secure: !!d.smtp_secure,
        smtp_user: d.smtp_user || '', smtp_from: d.smtp_from || '',
        smtp_pass_set: !!d.smtp_pass_set,
      });
      setPass('');
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const body = {
        keywords: (form.keywords || []).map(k => String(k).trim()).filter(Boolean),
        telegram_enabled: form.telegram_enabled,
        email_enabled: form.email_enabled,
        email_to: form.email_to,
        smtp_host: form.smtp_host, smtp_port: parseInt(form.smtp_port || 587, 10),
        smtp_secure: form.smtp_secure, smtp_user: form.smtp_user, smtp_from: form.smtp_from,
      };
      if (pass.trim()) body.smtp_pass = pass.trim();   // hanya kirim bila diisi
      await api('/api/sap/ase-alert/config', { method: 'PUT', body: JSON.stringify(body) });
      setMsg({ text: t('asealert.msgSaved'), type: 'success' });
      load();
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api('/api/sap/ase-alert/test', { method: 'POST', body: JSON.stringify({}) });
      setMsg({ text: ti('asealert.msgTest', { tg: r.telegram, email: r.email }), type: (r.telegram === 'ok' || r.email === 'ok') ? 'success' : 'info' });
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
    finally { setBusy(false); }
  };

  if (!form) return <Loading />;
  const kwText = (form.keywords || []).join('\n');
  const checkboxRow = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 10 };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>🚨 {t('asealert.title')}</span>
        <Badge text={form.telegram_enabled ? t('asealert.tgOn') : t('asealert.tgOff')} color={form.telegram_enabled ? '#10b981' : '#94a3b8'} />
        <Badge text={form.email_enabled ? t('asealert.emailOn') : t('asealert.emailOff')} color={form.email_enabled ? '#10b981' : '#94a3b8'} />
      </div>
      <p style={{ fontSize: 12, color: '#64748b', marginTop: 0, marginBottom: 16 }}>
        {t('asealert.descA')}<code>ZASE_LOG</code>{t('asealert.descB')}
      </p>

      {msg && <Alert msg={msg.text} type={msg.type} onClose={() => setMsg(null)} />}

      {/* Jadwal cron (reuse komponen yang ada) */}
      <CronScheduler api={api} jobKey="ase_log_alert" runConfirm={t('asealert.runConfirm')} />

      {/* Kata kunci */}
      <Field label={t('asealert.kwLabel')}>
        <textarea
          value={kwText}
          onChange={e => set('keywords', e.target.value.split('\n'))}
          rows={6}
          style={{ ...inputStyle, fontFamily: 'monospace', resize: 'vertical' }}
        />
      </Field>

      {/* Channel */}
      <label style={checkboxRow}>
        <input type="checkbox" checked={form.telegram_enabled} onChange={e => set('telegram_enabled', e.target.checked)} />
        {t('asealert.chTelegramA')}<code>notify</code>{t('asealert.chTelegramB')}
      </label>
      <label style={checkboxRow}>
        <input type="checkbox" checked={form.email_enabled} onChange={e => set('email_enabled', e.target.checked)} />
        {t('asealert.chEmail')}
      </label>

      {form.email_enabled && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 14, background: '#fafafa' }}>
          <Field label={t('asealert.recipients')}>
            <Input value={form.email_to} onChange={e => set('email_to', e.target.value)} placeholder="basis@perusahaan.com, ops@perusahaan.com" />
          </Field>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px' }}><Field label={t('asealert.smtpHost')}><Input value={form.smtp_host} onChange={e => set('smtp_host', e.target.value)} placeholder="smtp.perusahaan.com" /></Field></div>
            <div style={{ flex: '0 0 100px' }}><Field label={t('asealert.port')}><Input type="number" value={form.smtp_port} onChange={e => set('smtp_port', e.target.value)} /></Field></div>
          </div>
          <label style={checkboxRow}>
            <input type="checkbox" checked={form.smtp_secure} onChange={e => set('smtp_secure', e.target.checked)} />
            {t('asealert.ssl')}
          </label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px' }}><Field label={t('asealert.smtpUser')}><Input value={form.smtp_user} onChange={e => set('smtp_user', e.target.value)} placeholder="alert@perusahaan.com" /></Field></div>
            <div style={{ flex: '1 1 220px' }}><Field label={t('asealert.smtpPass') + (form.smtp_pass_set ? ' ' + t('asealert.smtpPassSaved') : '')}><Input type="password" value={pass} onChange={e => setPass(e.target.value)} placeholder={form.smtp_pass_set ? '••••••••' : ''} /></Field></div>
          </div>
          <Field label={t('asealert.from')}><Input value={form.smtp_from} onChange={e => set('smtp_from', e.target.value)} placeholder="alert@perusahaan.com" /></Field>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Btn variant="primary" onClick={save} disabled={busy}>💾 {t('common.save')}</Btn>
        <Btn variant="default" onClick={test} disabled={busy}>📨 {t('asealert.btnTest')}</Btn>
      </div>
    </div>
  );
}

// ── Database Maintenance (Debug / Vacuum / Backup / Restore) — admin-only ───────

function DatabasePanel({ api }) {
  const [stats, setStats]         = useState(null);
  const [aiStats, setAiStats]     = useState(null);
  const [alert, setAlert]         = useState(null);
  const [busy, setBusy]           = useState('');
  const [integrity, setIntegrity] = useState(null);
  const [aiIntegrity, setAiIntegrity] = useState(null);
  // --- AWS Price DB (aws_price.db) ---
  const [apStats, setApStats] = useState(null);
  const [apIntegrity, setApIntegrity] = useState(null);
  // --- Time-Series DB (dashboard_ts.db) ---
  const [tsStats, setTsStats] = useState(null);
  const [tsIntegrity, setTsIntegrity] = useState(null);
  const loadTsStats = useCallback(async () => {
    setBusy('ts-stats');
    try { setTsStats(await api('/api/tsdb/stats')); }
    catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  }, []);
  const runTsIntegrity = async () => {
    setBusy('ts-integrity'); setTsIntegrity(null);
    try {
      const r = await api('/api/tsdb/integrity', { method: 'POST', body: JSON.stringify({}) });
      setTsIntegrity(r);
      setAlert(r.ok ? { type: 'success', msg: 'Time-Series DB: ' + t('db.alert.integrityOk') } : { type: 'error', msg: 'Time-Series DB: ' + t('db.alert.integrityBad') });
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };
  const runTsVacuum = async () => {
    if (!window.confirm(t('db.confirm.vacuum'))) return;
    setBusy('ts-vacuum'); setAlert(null);
    try {
      const r = await api('/api/tsdb/vacuum', { method: 'POST', body: JSON.stringify({}) });
      setAlert({ type: 'success', msg: `Time-Series DB VACUUM selesai. Sebelum: ${fmtBytes(r.before)}, Sesudah: ${fmtBytes(r.after)}, Reclaimed: ${fmtBytes(r.freed)}` });
      loadTsStats();
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };
  const loadApStats = useCallback(async () => {
    setBusy('ap-stats');
    try { setApStats(await api('/api/awsprice-db/stats')); }
    catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  }, []);
  const runApIntegrity = async () => {
    setBusy('ap-integrity'); setApIntegrity(null);
    try {
      const r = await api('/api/awsprice-db/integrity', { method: 'POST', body: JSON.stringify({}) });
      setApIntegrity(r);
      setAlert(r.ok ? { type: 'success', msg: 'AWS Price DB: ' + t('db.alert.integrityOk') } : { type: 'error', msg: 'AWS Price DB: ' + t('db.alert.integrityBad') });
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };
  const runApVacuum = async () => {
    if (!window.confirm(t('db.confirm.vacuum'))) return;
    setBusy('ap-vacuum'); setAlert(null);
    try {
      const r = await api('/api/awsprice-db/vacuum', { method: 'POST', body: JSON.stringify({}) });
      setAlert({ type: 'success', msg: `AWS Price DB VACUUM selesai. Sebelum: ${fmtBytes(r.before)}, Sesudah: ${fmtBytes(r.after)}, Reclaimed: ${fmtBytes(r.freed)}` });
      loadApStats();
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };
  const restoreRef = useRef(null);
  const { t } = useLang();
  const ti = (key, vars) => {
    let s = t(key);
    if (vars) for (const k in vars) s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), () => String(vars[k]));
    return s;
  };

  const loadStats = useCallback(async () => {
    setBusy('stats');
    try { setStats(await api('/api/db/stats')); }
    catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(prev => prev === 'stats' ? '' : prev); }
  }, [api]);

  const loadAiStats = useCallback(async () => {
    setBusy('ai-stats');
    try { setAiStats(await api('/api/ai-db/stats')); }
    catch (e) { setAlert({ type: 'error', msg: 'AI Service: ' + e.message }); }
    finally { setBusy(prev => prev === 'ai-stats' ? '' : prev); }
  }, [api]);

  useEffect(() => { loadStats(); loadAiStats(); loadApStats(); loadTsStats(); }, [loadStats, loadAiStats, loadApStats, loadTsStats]);

  const runIntegrity = async () => {
    setBusy('integrity'); setAlert(null); setIntegrity(null);
    try {
      const r = await api('/api/db/integrity', { method: 'POST', body: JSON.stringify({}) });
      setIntegrity(r);
      setAlert(r.ok ? { type: 'success', msg: t('db.alert.integrityOk') } : { type: 'error', msg: t('db.alert.integrityBad') });
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };

  const runAiIntegrity = async () => {
    setBusy('ai-integrity'); setAlert(null); setAiIntegrity(null);
    try {
      const r = await api('/api/ai-db/integrity', { method: 'POST', body: JSON.stringify({}) });
      setAiIntegrity(r);
      setAlert(r.ok ? { type: 'success', msg: 'AI Service: ' + t('db.alert.integrityOk') } : { type: 'error', msg: 'AI Service: ' + t('db.alert.integrityBad') });
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };

  const runVacuum = async () => {
    if (!window.confirm(t('db.confirm.vacuum'))) return;
    setBusy('vacuum'); setAlert(null);
    try {
      const r = await api('/api/db/vacuum', { method: 'POST', body: JSON.stringify({}) });
      setAlert({ type: 'success', msg: ti('db.alert.vacuumOk', { before: fmtBytes(r.before), after: fmtBytes(r.after), reclaimed: fmtBytes(r.reclaimed) }) });
      loadStats();
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };

  const runAiVacuum = async () => {
    if (!window.confirm(t('tblMaint.jalankanVacuumAiServiceDb'))) return;
    setBusy('ai-vacuum'); setAlert(null);
    try {
      const r = await api('/api/ai-db/vacuum', { method: 'POST', body: JSON.stringify({}) });
      setAlert({ type: 'success', msg: `AI Service VACUUM selesai. Sebelum: ${fmtBytes(r.before)}, Sesudah: ${fmtBytes(r.after)}, Reclaimed: ${fmtBytes(r.reclaimed)}` });
      loadAiStats();
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };

  const doBackup = async () => {
    setBusy('backup'); setAlert(null);
    try {
      const res = await axiosApi.get('/api/db/backup', { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m  = cd.match(/filename="?([^"]+)"?/);
      const name = (m && m[1]) || `dashboard-backup-${new Date().toISOString().slice(0, 10)}.db`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/octet-stream' }));
      const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      setAlert({ type: 'success', msg: ti('db.alert.backupOk', { name }) });
    } catch (e) {
      setAlert({ type: 'error', msg: (e.response?.data?.error) || e.message || 'Backup error.' });
    } finally { setBusy(''); }
  };

  const doAiBackup = async () => {
    setBusy('ai-backup'); setAlert(null);
    try {
      const res = await axiosApi.get('/api/ai-db/backup', { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m  = cd.match(/filename="?([^"]+)"?/);
      const name = (m && m[1]) || `ai-service-backup-${new Date().toISOString().slice(0, 10)}.db`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/octet-stream' }));
      const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      setAlert({ type: 'success', msg: `AI Service backup berhasil: ${name}` });
    } catch (e) {
      setAlert({ type: 'error', msg: (e.response?.data?.error) || e.message || 'Backup error.' });
    } finally { setBusy(''); }
  };

  const onRestoreFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!window.confirm(ti('db.confirm.restore', { name: file.name }))) return;
    setBusy('restore'); setAlert(null); setIntegrity(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axiosApi.post('/api/db/restore', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data || {};
      setAlert({ type: 'success', msg: ti('db.alert.restoreOk', { message: d.message || '', n: (d.tables || []).length }) });
      loadStats();
    } catch (err) {
      setAlert({ type: 'error', msg: (err.response?.data?.error) || err.message || 'Restore error.' });
    } finally { setBusy(''); }
  };

  const cardStyle = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px' };
  const statBox   = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', minWidth: 120 };
  const statLabel = { fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 };
  const statVal   = { fontSize: 18, color: '#0f172a', fontWeight: 700, marginTop: 2 };

  const StatsGrid = ({ s }) => s ? (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
      <div style={statBox}><div style={statLabel}>{t('db.stats.fileSize')}</div><div style={statVal}>{fmtBytes(s.fileSizeBytes)}</div></div>
      <div style={statBox}><div style={statLabel}>{t('db.stats.tables')}</div><div style={statVal}>{fmt(s.tableCount)}</div></div>
      <div style={statBox}><div style={statLabel}>{t('db.stats.totalRows')}</div><div style={statVal}>{fmt(s.totalRows)}</div></div>
      <div style={statBox}><div style={statLabel}>{t('db.stats.pages')}</div><div style={statVal}>{fmt(s.pageCount)}</div></div>
      <div style={statBox}><div style={statLabel}>{t('db.stats.freelist')}</div><div style={statVal}>{fmt(s.freelistCount)}</div></div>
      <div style={statBox}><div style={statLabel}>{t('db.stats.reclaimable')}</div><div style={statVal}>{fmtBytes(s.reclaimableBytes)}</div></div>
    </div>
  ) : <div style={{ fontSize: 13, color: '#94a3b8' }}>{t('db.loading')}</div>;

  const IntegrityResult = ({ r }) => !r ? null : (
    <div style={{ ...cardStyle, marginTop: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: r.ok ? '#15803d' : '#b91c1c', marginBottom: 8 }}>
        {r.ok ? '✅ ' + t('db.integrity.healthy') : '⚠️ ' + t('db.integrity.problem')}
      </div>
      <pre style={{ margin: 0, fontSize: 12, color: '#334155', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {(r.integrity || []).join('\n')}
      </pre>
      {r.foreignKeys && r.foreignKeys.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c', margin: '10px 0 4px' }}>{t('db.integrity.fkBad')}</div>
          <pre style={{ margin: 0, fontSize: 12, color: '#334155', whiteSpace: 'pre-wrap' }}>{JSON.stringify(r.foreignKeys, null, 2)}</pre>
        </>
      )}
    </div>
  );

  return (
    <div>
      <Alert msg={alert?.msg} type={alert?.type} onClose={() => setAlert(null)} />
      {/* ── Dashboard DB ── */}
      <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 10 }}>🗄️ Dashboard DB</div>
      <div style={{ ...cardStyle, marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>📊 {t('db.stats.title')}</span>
          <Btn size="sm" disabled={busy === 'stats'} onClick={loadStats}>{busy === 'stats' ? '⏳' : '🔄'} {t('db.btn.refresh')}</Btn>
        </div>
        <StatsGrid s={stats} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <Btn disabled={!!busy} onClick={runIntegrity}>{busy === 'integrity' ? '⏳ ' + t('db.btn.integrityRunning') : '🔍 ' + t('db.btn.integrity')}</Btn>
        <Btn disabled={!!busy} onClick={runVacuum}>{busy === 'vacuum' ? '⏳ ' + t('db.btn.vacuumRunning') : '🧹 ' + t('db.btn.vacuum')}</Btn>
        <Btn variant="primary" disabled={!!busy} onClick={doBackup}>{busy === 'backup' ? '⏳ ' + t('db.btn.backupRunning') : '⬇️ ' + t('db.btn.backup')}</Btn>
        <input ref={restoreRef} type="file" accept=".db,.sqlite,.sqlite3,application/octet-stream" style={{ display: 'none' }} onChange={onRestoreFile} />
        <Btn variant="danger" disabled={!!busy} onClick={() => restoreRef.current && restoreRef.current.click()}>
          {busy === 'restore' ? '⏳ ' + t('db.btn.restoreRunning') : '⬆️ ' + t('db.btn.restore')}
        </Btn>
      </div>
      <div style={{ fontSize: 12, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px', marginBottom: 14 }}>
        <b>{t('db.warn.boldA')}</b>{t('db.warn.mid')}<b>{t('db.warn.boldB')}</b>{t('db.warn.end')}
      </div>
      <IntegrityResult r={integrity} />
      {/* ── Divider ── */}
      <div style={{ borderTop: '2px dashed #e2e8f0', margin: '24px 0 16px' }} />
      {/* ── AI Service DB ── */}
      <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 10 }}>🤖 AI Service DB</div>
      <div style={{ ...cardStyle, marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>📊 {t('db.stats.title')}</span>
          <Btn size="sm" disabled={busy === 'ai-stats'} onClick={loadAiStats}>{busy === 'ai-stats' ? '⏳' : '🔄'} {t('db.btn.refresh')}</Btn>
        </div>
        <StatsGrid s={aiStats} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <Btn disabled={!!busy} onClick={runAiIntegrity}>{busy === 'ai-integrity' ? '⏳ ' + t('db.btn.integrityRunning') : '🔍 ' + t('db.btn.integrity')}</Btn>
        <Btn disabled={!!busy} onClick={runAiVacuum}>{busy === 'ai-vacuum' ? '⏳ ' + t('db.btn.vacuumRunning') : '🧹 ' + t('db.btn.vacuum')}</Btn>
        <Btn variant="primary" disabled={!!busy} onClick={doAiBackup}>{busy === 'ai-backup' ? '⏳ ' + t('db.btn.backupRunning') : '⬇️ ' + t('db.btn.backup')}</Btn>
      </div>
      <div style={{ fontSize: 12, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', marginBottom: 14 }}>
        {t('tblMaint.aiServiceDbOperasiRestore')} <code style={{ background: '#eef2ff', padding: '1px 5px', borderRadius: 4 }}>ai_service.db</code>langsung di server.
              </div>
      <IntegrityResult r={aiIntegrity} />
          {/* — Divider — */}
          <div style={{ borderTop: '2px dashed #e2e8f0', margin: '24px 0 16px' }} />
          {/* — AWS Price DB — */}
          <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 10 }}>&#128176; AWS Price DB</div>
          <div style={{ ...cardStyle, marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>&#128202; {t('db.stats.title')}</span>
              <Btn size="sm" disabled={!!busy} onClick={loadApStats}>{busy === 'ap-stats' ? '\u23f3' : '\u21bb'} {t('db.btn.refresh')}</Btn>
            </div>
            <StatsGrid s={apStats} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            <Btn disabled={!!busy} onClick={runApIntegrity}>{busy === 'ap-integrity' ? '\u23f3' : '\u{1f50d}'} {t('db.btn.integrity')}</Btn>
            <Btn disabled={!!busy} onClick={runApVacuum}>{busy === 'ap-vacuum' ? '\u23f3' : '\u{1f9f9}'} {t('db.btn.vacuum')}</Btn>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
            &#8505; Katalog harga AWS (aws_price_product, aws_price_term) dipisah ke file aws_price.db, di-ATTACH otomatis sebagai schema awsprice. Data ini bisa dibangun ulang dari AWS Price List API lewat halaman AWS Pricing Calculator, jadi tidak disediakan backup/restore di sini.
          </div>
          <IntegrityResult r={apIntegrity} />
          {/* — Divider — */}
          <div style={{ borderTop: '2px dashed #e2e8f0', margin: '24px 0 16px' }} />
          {/* — Time-Series DB — */}
          <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 10 }}>&#128200; Time-Series DB</div>
          <div style={{ ...cardStyle, marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>&#128202; {t('db.stats.title')}</span>
              <Btn size="sm" disabled={!!busy} onClick={loadTsStats}>{busy === 'ts-stats' ? '\u23f3' : '\u21bb'} {t('db.btn.refresh')}</Btn>
            </div>
            <StatsGrid s={tsStats} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            <Btn disabled={!!busy} onClick={runTsIntegrity}>{busy === 'ts-integrity' ? '\u23f3' : '\u{1f50d}'} {t('db.btn.integrity')}</Btn>
            <Btn disabled={!!busy} onClick={runTsVacuum}>{busy === 'ts-vacuum' ? '\u23f3' : '\u{1f9f9}'} {t('db.btn.vacuum')}</Btn>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
            &#8505; dashboard_ts.db berisi sap_usr_utl_raw (audit login user SAP) dan aws_cloudtrail_events, di-ATTACH otomatis sebagai schema ts. Dipisah karena append-only dan mendominasi pertumbuhan. Berbeda dengan AWS Price DB, file ini WAJIB di-backup (audit login tidak bisa dibangun ulang) — cron harian 00:30 ke /backup/dashboard-ts-db, retensi 14 hari.
          </div>
          <IntegrityResult r={tsIntegrity} />
    </div>
  );
}

// ── M365 License Panel (Stage 3) ────────────────────────────────────────────
// Baca/tulis via endpoint generik /api/sql/execute (admin-only). Opsi A.
function M365Panel({ api }) {
  const { t } = useLang();
  const [alert, setAlert]       = useState(null);
  const [snap, setSnap]         = useState('');
  const [cats, setCats]         = useState([]);      // urutan kategori (friendly_name)
  const [summary, setSummary]   = useState([]);      // baris m365_license_summary
  const [domains, setDomains]   = useState([]);      // config domain
  const [skus, setSkus]         = useState([]);      // config SKU
  const [users, setUsers]       = useState({ rows: [], total: 0 });
  const [uFilter, setUFilter]   = useState('');
  const [uPage, setUPage]       = useState(0);
  const [busy, setBusy]         = useState(false);
  const PAGE = 50;

  const esc = (v) => String(v).replace(/'/g, "''");
  const num = (v) => { const n = parseInt(v, 10); return isNaN(n) ? 0 : n; };

  // Jalankan SQL lewat endpoint generik. write=true untuk non-SELECT.
  const exec = useCallback(async (sql, write = false) => {
    return api('/api/sql/execute', { method: 'POST', body: JSON.stringify({ sql, allowWrite: write }) });
  }, [api]);

  const loadConfig = useCallback(async () => {
    try {
      const d = await exec('SELECT domain, sort_order, active FROM m365_domains ORDER BY sort_order, domain');
      setDomains(d.rows || []);
      const s = await exec('SELECT sku_part, friendly_name, is_category, sort_order FROM m365_sku_names ORDER BY sort_order, sku_part');
      setSkus(s.rows || []);
      setCats((s.rows || []).filter(r => r.is_category).map(r => r.friendly_name));
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
  }, [exec]);

  const loadSummary = useCallback(async () => {
    try {
      const d = await exec('SELECT domain, license, count FROM m365_license_summary ORDER BY domain, license');
      setSummary(d.rows || []);
      const m = await exec('SELECT MAX(snapshot_at) AS snap FROM m365_license_summary');
      setSnap(m.rows?.[0]?.snap || '');
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
  }, [exec]);

  const loadUsers = useCallback(async (page = 0, filter = '') => {
    try {
      const where = filter.trim() ? ` WHERE upn LIKE '%${esc(filter.trim())}%' OR domain LIKE '%${esc(filter.trim())}%'` : '';
      const c = await exec(`SELECT COUNT(*) AS n FROM m365_user_license${where}`);
      const total = c.rows?.[0]?.n || 0;
      const d = await exec(`SELECT upn, domain, license, sku_part FROM m365_user_license${where} ORDER BY upn, license LIMIT ${PAGE} OFFSET ${page * PAGE}`);
      setUsers({ rows: d.rows || [], total });
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
  }, [exec]);

  useEffect(() => { loadConfig(); loadSummary(); loadUsers(0, ''); }, [loadConfig, loadSummary, loadUsers]);

  // Pivot ringkasan: domain × kategori
  const pivot = (() => {
    const byDomain = {};
    summary.forEach(r => { (byDomain[r.domain] = byDomain[r.domain] || {})[r.license] = r.count; });
    const rows = Object.keys(byDomain).sort().map(dom => {
      const row = { domain: dom, total: 0 };
      cats.forEach(c => { const v = byDomain[dom][c] || 0; row[c] = v; row.total += v; });
      return row;
    });
    const grand = { domain: 'GRAND TOTAL', total: 0 };
    cats.forEach(c => { grand[c] = rows.reduce((a, r) => a + (r[c] || 0), 0); grand.total += grand[c]; });
    return { rows, grand };
  })();

  // ── Config: domain ──
  const addDomain = async () => {
    const dom = window.prompt('Domain baru (mis. contoso.com):');
    if (!dom) return;
    const ord = domains.length;
    setBusy(true); setAlert(null);
    try {
      await exec(`INSERT INTO m365_domains (domain,sort_order,active) VALUES ('${esc(dom.trim().toLowerCase())}',${ord},1)`, true);
      setAlert({ type: 'success', msg: `Domain "${dom}" ditambahkan.` });
      loadConfig();
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(false); }
  };
  const toggleDomain = async (dom, active) => {
    setBusy(true); setAlert(null);
    try { await exec(`UPDATE m365_domains SET active=${active ? 0 : 1} WHERE domain='${esc(dom)}'`, true); loadConfig(); }
    catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(false); }
  };
  const delDomain = async (dom) => {
    if (!window.confirm(t('tblMaint.hapusDomainDaftarRingkasan', {
      dom
    }))) return;
    setBusy(true); setAlert(null);
    try { await exec(`DELETE FROM m365_domains WHERE domain='${esc(dom)}'`, true); loadConfig(); }
    catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(false); }
  };

  // ── Config: SKU ──
  const addSku = async () => {
    const part = window.prompt('SKU part number (mis. SPE_E3):'); if (!part) return;
    const name = window.prompt('Nama ramah (mis. Microsoft 365 E3):'); if (!name) return;
    const isCat = window.confirm(t('tblMaint.tampilkanSebagaiKolomKategoriRingkasan'));
    setBusy(true); setAlert(null);
    try {
      await exec(`INSERT INTO m365_sku_names (sku_part,friendly_name,is_category,sort_order) VALUES ('${esc(part.trim())}','${esc(name.trim())}',${isCat ? 1 : 0},${skus.length})`, true);
      setAlert({ type: 'success', msg: `SKU "${part}" ditambahkan.` });
      loadConfig();
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(false); }
  };
  const toggleCat = async (part, isCat) => {
    setBusy(true); setAlert(null);
    try { await exec(`UPDATE m365_sku_names SET is_category=${isCat ? 0 : 1} WHERE sku_part='${esc(part)}'`, true); loadConfig(); }
    catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(false); }
  };
  const renameSku = async (part, cur) => {
    const name = window.prompt('Nama ramah baru:', cur); if (name == null) return;
    setBusy(true); setAlert(null);
    try { await exec(`UPDATE m365_sku_names SET friendly_name='${esc(name.trim())}' WHERE sku_part='${esc(part)}'`, true); loadConfig(); }
    catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(false); }
  };
  const delSku = async (part) => {
    if (!window.confirm(t('tblMaint.hapusMappingSku', {
      part
    }))) return;
    setBusy(true); setAlert(null);
    try { await exec(`DELETE FROM m365_sku_names WHERE sku_part='${esc(part)}'`, true); loadConfig(); }
    catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(false); }
  };

  const th = { padding: '8px 10px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#334155', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
  const td = { padding: '7px 10px', textAlign: 'center', fontSize: 13, color: '#0f172a', borderBottom: '1px solid #f1f5f9' };
  const sect = { fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '18px 0 8px' };
  const totalPages = Math.max(1, Math.ceil(users.total / PAGE));

  return (
    <div>
      {alert && <Alert msg={alert.msg} type={alert.type} onClose={() => setAlert(null)} />}
      {/* Jadwal — reuse komponen cron yang ada */}
      <CronScheduler api={api} jobKey="m365_license" runConfirm="Jalankan fetch M365 License sekarang?" />
      {/* Ringkasan per domain */}
      <div style={sect}>{t('tblMaint.ringkasanLisensiPerDomain')}</div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
        {snap ? t('tblMaint.snapshotTerakhir', {
          snap
        }) : t('tblMaint.belumAdaDataKlikRun')}
        {'  '}<Btn size="sm" onClick={() => { loadSummary(); loadConfig(); }}>🔄 Refresh</Btn>
      </div>
      {pivot.rows.length === 0 ? <Empty icon="📭" text={t('tblMaint.belumAdaDataRingkasan')} /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left' }}>Domain</th>
              {cats.map(c => <th key={c} style={th}>{c}</th>)}
              <th style={th}>{t('tblMaint.total')}</th>
            </tr></thead>
            <tbody>
              {pivot.rows.map(r => (
                <tr key={r.domain}>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{r.domain}</td>
                  {cats.map(c => <td key={c} style={td}>{r[c] || 0}</td>)}
                  <td style={{ ...td, fontWeight: 700 }}>{r.total}</td>
                </tr>
              ))}
              <tr style={{ background: '#f8fafc' }}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 800 }}>{pivot.grand.domain}</td>
                {cats.map(c => <td key={c} style={{ ...td, fontWeight: 800 }}>{pivot.grand[c]}</td>)}
                <td style={{ ...td, fontWeight: 800 }}>{pivot.grand.total}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {/* Detail user */}
      <div style={sect}>👤 Detail Lisensi per User <Badge text={t('tblMaint.baris', {
        total: users.total
      })} color="rgb(var(--brand-500))" /></div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <input value={uFilter} onChange={e => setUFilter(e.target.value)} placeholder={t('tblMaint.cariUpnDomain')}
          onKeyDown={e => { if (e.key === 'Enter') { setUPage(0); loadUsers(0, uFilter); } }}
          style={{ ...inputStyle, width: 220 }} />
        <Btn size="sm" onClick={() => { setUPage(0); loadUsers(0, uFilter); }}>{t('tblMaint.cari')}</Btn>
        {uFilter && <Btn size="sm" onClick={() => { setUFilter(''); setUPage(0); loadUsers(0, ''); }}>Reset</Btn>}
      </div>
      {users.rows.length === 0 ? <Empty icon="📭" text={t('tblMaint.belumAdaDataUser')} /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left' }}>UPN</th>
              <th style={{ ...th, textAlign: 'left' }}>Domain</th>
              <th style={{ ...th, textAlign: 'left' }}>License</th>
              <th style={th}>SKU Part</th>
            </tr></thead>
            <tbody>
              {users.rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ ...td, textAlign: 'left' }}>{r.upn}</td>
                  <td style={{ ...td, textAlign: 'left' }}>{r.domain}</td>
                  <td style={{ ...td, textAlign: 'left' }}>{r.license}</td>
                  <td style={{ ...td, color: '#64748b', fontFamily: 'monospace', fontSize: 12 }}>{r.sku_part || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {users.total > PAGE && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, fontSize: 13 }}>
          <Btn size="sm" disabled={uPage === 0} onClick={() => { const p = uPage - 1; setUPage(p); loadUsers(p, uFilter); }}>← Prev</Btn>
          <span style={{ color: '#64748b' }}>Hal {uPage + 1} / {totalPages}</span>
          <Btn size="sm" disabled={uPage + 1 >= totalPages} onClick={() => { const p = uPage + 1; setUPage(p); loadUsers(p, uFilter); }}>Next →</Btn>
        </div>
      )}
      {/* Config: domain */}
      <div style={sect}>🌐 Konfigurasi Domain <Btn size="sm" variant="primary" disabled={busy} onClick={addDomain}>+ Domain</Btn></div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead><tr>
            <th style={{ ...th, textAlign: 'left' }}>Domain</th><th style={th}>Urutan</th><th style={th}>{t('tblMaint.aktif')}</th><th style={th}>Aksi</th>
          </tr></thead>
          <tbody>
            {domains.map(d => (
              <tr key={d.domain}>
                <td style={{ ...td, textAlign: 'left' }}>{d.domain}</td>
                <td style={td}>{d.sort_order}</td>
                <td style={td}>{d.active ? <Badge text={t('tblMaint.aktif')} color="#10b981" /> : <Badge text={t('tblMaint.nonaktif')} color="#94a3b8" />}</td>
                <td style={td}>
                  <Btn size="sm" disabled={busy} onClick={() => toggleDomain(d.domain, d.active)}>{d.active ? 'Nonaktifkan' : 'Aktifkan'}</Btn>{' '}
                  <Btn size="sm" variant="danger" disabled={busy} onClick={() => delDomain(d.domain)}>{t('tblMaint.hapus2')}</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Config: SKU */}
      <div style={sect}>🏷️ Konfigurasi SKU / Lisensi <Btn size="sm" variant="primary" disabled={busy} onClick={addSku}>+ SKU</Btn></div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>{t('tblMaint.kolomKategoriTampilSebagaiKolom')}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead><tr>
            <th style={{ ...th, textAlign: 'left' }}>SKU Part</th><th style={{ ...th, textAlign: 'left' }}>{t('tblMaint.namaRamah')}</th><th style={th}>Kategori</th><th style={th}>Urutan</th><th style={th}>Aksi</th>
          </tr></thead>
          <tbody>
            {skus.map(s => (
              <tr key={s.sku_part}>
                <td style={{ ...td, textAlign: 'left', fontFamily: 'monospace', fontSize: 12 }}>{s.sku_part}</td>
                <td style={{ ...td, textAlign: 'left' }}>{s.friendly_name}</td>
                <td style={td}>{s.is_category ? <Badge text="Ya" color="#10b981" /> : <Badge text="—" color="#94a3b8" />}</td>
                <td style={td}>{s.sort_order}</td>
                <td style={td}>
                  <Btn size="sm" disabled={busy} onClick={() => renameSku(s.sku_part, s.friendly_name)}>Rename</Btn>{' '}
                  <Btn size="sm" disabled={busy} onClick={() => toggleCat(s.sku_part, s.is_category)}>{s.is_category ? 'Bukan kategori' : 'Jadikan kategori'}</Btn>{' '}
                  <Btn size="sm" variant="danger" disabled={busy} onClick={() => delSku(s.sku_part)}>{t('tblMaint.hapus2')}</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tab "SDP Cloud": cron scheduler + ringkasan requests & assets (read-only) ──
function SdpPanel({ api }) {
  const {
    t
  } = useLang();

  const [alert, setAlert]       = useState(null);
  const [snap, setSnap]         = useState('');
  const [counts, setCounts]     = useState({ requests: 0, assets: 0 });
  const [requests, setRequests] = useState([]);
  const [assets, setAssets]     = useState([]);

  const exec = useCallback(async (sql) => {
    return api('/api/sql/execute', { method: 'POST', body: JSON.stringify({ sql, allowWrite: false }) });
  }, [api]);

  const load = useCallback(async () => {
    try {
      const cr = await exec('SELECT COUNT(*) AS n FROM sdp_requests');
      const ca = await exec('SELECT COUNT(*) AS n FROM sdp_assets');
      setCounts({ requests: cr.rows?.[0]?.n || 0, assets: ca.rows?.[0]?.n || 0 });
      const m = await exec('SELECT MAX(snapshot_at) AS snap FROM sdp_requests');
      setSnap(m.rows?.[0]?.snap || '');
      const r = await exec('SELECT display_id, subject, status, requester_name, site, created_time FROM sdp_requests ORDER BY created_time DESC LIMIT 20');
      setRequests(r.rows || []);
      const a = await exec('SELECT name, product_type, state, site, created_time FROM sdp_assets ORDER BY created_time DESC LIMIT 20');
      setAssets(a.rows || []);
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
  }, [exec]);

  useEffect(() => { load(); }, [load]);

  const th   = { padding: '8px 10px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#334155', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
  const td   = { padding: '7px 10px', textAlign: 'left', fontSize: 13, color: '#0f172a', borderBottom: '1px solid #f1f5f9' };
  const sect = { fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '18px 0 8px' };

  return (
    <div>
      {alert && <Alert msg={alert.msg} type={alert.type} onClose={() => setAlert(null)} />}
      {/* Jadwal — reuse komponen cron yang ada */}
      <CronScheduler api={api} jobKey="sdp" runConfirm="Jalankan fetch ManageEngine SDP Cloud sekarang?" />
      <div style={{ display: 'flex', gap: 12, margin: '14px 0' }}>
        <StatCard label={t('tblMaint.totalRequests')} value={counts.requests} />
        <StatCard label={t('tblMaint.totalAssets')} value={counts.assets} />
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
        {snap ? t('tblMaint.snapshotTerakhir', {
          snap
        }) : t('tblMaint.belumAdaDataKlikRun')}
      </div>
      <div style={sect}>🎫 Requests (20 terbaru)</div>
      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>Display ID</th><th style={th}>Subject</th><th style={th}>Status</th>
            <th style={th}>Requester</th><th style={th}>Site</th><th style={th}>Created</th>
          </tr></thead>
          <tbody>
            {requests.length === 0
              ? <tr><td style={td} colSpan={6}>—</td></tr>
              : requests.map((r, i) => (
                <tr key={i}>
                  <td style={td}>{r.display_id}</td><td style={td}>{r.subject}</td><td style={td}>{r.status}</td>
                  <td style={td}>{r.requester_name}</td><td style={td}>{r.site}</td><td style={td}>{r.created_time}</td>
                </tr>))}
          </tbody>
        </table>
      </div>
      <div style={sect}>💻 Assets (20 terbaru)</div>
      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>Name</th><th style={th}>Product Type</th><th style={th}>State</th>
            <th style={th}>Site</th><th style={th}>Created</th>
          </tr></thead>
          <tbody>
            {assets.length === 0
              ? <tr><td style={td} colSpan={5}>—</td></tr>
              : assets.map((a, i) => (
                <tr key={i}>
                  <td style={td}>{a.name}</td><td style={td}>{a.product_type}</td><td style={td}>{a.state}</td>
                  <td style={td}>{a.site}</td><td style={td}>{a.created_time}</td>
                </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tab "Purge Data": pengosongan tabel manual terkontrol ─────────────────────
// Grup 1 = SAP Role Maintenance (semua bertkolom connection_id).
// Grup 2 = kandidat lain (log, history, snapshot) — sebagian global.
// Semua penghapusan lewat whitelist di backend; UI hanya mengirim nama tabel.
function PurgePanel({ api }) {
  const [conns, setConns]     = useState([]);
  const [connId, setConnId]   = useState('');      // '' = semua landscape
  const [days, setDays]       = useState(0);
  const [cat, setCat]         = useState(null);
  const [sel, setSel]         = useState({});      // { [group]: Set(namaTabel) }
  const [alert, setAlert]     = useState(null);
  const [busy, setBusy]       = useState('');
  const [confirmFor, setConfirmFor] = useState(null); // { group, tables }
  const [confirmTxt, setConfirmTxt] = useState('');
  const [result, setResult]   = useState(null);

  const loadConns = useCallback(async () => {
    try { setConns(await api('/api/sap/connections') || []); }
    catch (e) { setAlert({ type: 'error', msg: e.message }); }
  }, [api]);

  const loadCat = useCallback(async () => {
    setBusy('cat');
    try {
      const qs = [];
      if (connId) qs.push('connectionId=' + encodeURIComponent(connId));
      if (days > 0) qs.push('olderThanDays=' + days);
      setCat(await api('/api/purge/catalog' + (qs.length ? '?' + qs.join('&') : '')));
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  }, [api, connId, days]);

  useEffect(() => { loadConns(); }, [loadConns]);
  useEffect(() => { loadCat(); }, [loadCat]);

  const connLabel = (c) => `${c.landscape}${c.environment ? ' / ' + c.environment : ''}${c.sid ? ' (' + c.sid + ')' : ''}`;
  const curConnLabel = () => {
    if (!connId) return 'SEMUA LANDSCAPE';
    const c = conns.find(x => String(x.id) === String(connId));
    return c ? connLabel(c) : ('#' + connId);
  };

  const isSel = (g, n) => !!(sel[g] && sel[g][n]);
  const toggle = (g, n) => setSel(p => ({ ...p, [g]: { ...(p[g] || {}), [n]: !((p[g] || {})[n]) } }));
  const toggleAll = (g, tables, on) => setSel(p => {
    const next = {};
    for (const t of tables) next[t.name] = on;
    return { ...p, [g]: next };
  });
  const selectedOf = (g) => Object.keys(sel[g] || {}).filter(n => sel[g][n]);

  // Baris yang akan terhapus untuk pilihan saat ini (scoped bila ada, else total).
  const affectedRows = (g) => {
    if (!cat || !cat.groups[g]) return 0;
    const chosen = selectedOf(g);
    return cat.groups[g].tables
      .filter(t => chosen.includes(t.name))
      .reduce((s, t) => s + (t.scoped !== null && t.scoped !== undefined ? t.scoped : t.total), 0);
  };

  const askPurge = (g) => {
    const tables = selectedOf(g);
    if (!tables.length) { setAlert({ type: 'error', msg: 'Pilih minimal satu tabel.' }); return; }
    setConfirmTxt(''); setResult(null); setConfirmFor({ group: g, tables });
  };

  const doPurge = async () => {
    const { group, tables } = confirmFor;
    setBusy('purge'); setAlert(null);
    try {
      const r = await api('/api/purge/run', {
        method: 'POST',
        body: JSON.stringify({
          group, tables, confirm: confirmTxt.trim().toUpperCase(),
          connectionId: connId || null, olderThanDays: days || 0,
        }),
      });
      setResult(r);
      setAlert({
        type: r.ok ? 'success' : 'error',
        msg: `Purge selesai — ${fmt(r.totalDeleted)} baris dihapus dari ${tables.length} tabel.`
          + (r.ok ? '' : ' Sebagian tabel gagal, lihat rincian.'),
      });
      setConfirmFor(null);
      setSel(p => ({ ...p, [group]: {} }));
      loadCat();
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };

  const runVacuum = async () => {
    if (!window.confirm('Jalankan VACUUM sekarang? Proses ini menulis ulang seluruh file DB (~146 MB) dan memblokir tulis lain. Sebaiknya di luar jam sibuk.')) return;
    setBusy('vacuum'); setAlert(null);
    try {
      const r = await api('/api/db/vacuum', { method: 'POST', body: JSON.stringify({}) });
      setAlert({ type: 'success', msg: `VACUUM selesai. Sebelum: ${fmtBytes(r.before)}, Sesudah: ${fmtBytes(r.after)}, Reclaimed: ${fmtBytes(r.reclaimed)}` });
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };

  const th   = { padding: '8px 10px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#334155', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
  const td   = { padding: '7px 10px', textAlign: 'left', fontSize: 13, color: '#0f172a', borderBottom: '1px solid #f1f5f9' };
  const tdN  = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const card = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', marginBottom: 16 };

  const GroupCard = ({ gkey, icon }) => {
    const g = cat && cat.groups ? cat.groups[gkey] : null;
    if (!g) return null;
    const chosen = selectedOf(gkey);
    const allOn  = chosen.length === g.tables.length;
    return (
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{icon} {g.label}</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Btn size="sm" onClick={() => toggleAll(gkey, g.tables, !allOn)}>
              {allOn ? '☐ Batal pilih semua' : '☑️ Pilih semua'}
            </Btn>
            <Btn size="sm" variant="danger" disabled={!!busy || !chosen.length} onClick={() => askPurge(gkey)}>
              🗑️ Purge terpilih ({chosen.length})
            </Btn>
          </div>
        </div>
        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...th, width: 36 }}></th>
              <th style={th}>Tabel</th>
              <th style={th}>Keterangan</th>
              <th style={th}>Scope</th>
              <th style={{ ...th, textAlign: 'right' }}>Total Baris</th>
              <th style={{ ...th, textAlign: 'right' }}>Akan Dihapus</th>
            </tr></thead>
            <tbody>
              {g.tables.map(t => {
                const hit = (t.scoped !== null && t.scoped !== undefined) ? t.scoped : t.total;
                return (
                  <tr key={t.name} style={isSel(gkey, t.name) ? { background: '#fff7ed' } : undefined}>
                    <td style={td}>
                      <input type="checkbox" checked={isSel(gkey, t.name)} onChange={() => toggle(gkey, t.name)} />
                    </td>
                    <td style={{ ...td, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{t.name}</td>
                    <td style={td}>{t.label}</td>
                    <td style={{ ...td, fontSize: 11, color: '#64748b' }}>
                      {t.connScoped ? '🔗 per landscape' : '🌐 global'}
                      {t.dateCol ? ' · 📅 ' + t.dateCol : ''}
                    </td>
                    <td style={tdN}>{t.error ? '—' : fmt(t.total)}</td>
                    <td style={{ ...tdN, fontWeight: 700, color: hit > 0 ? '#b45309' : '#94a3b8' }}>
                      {t.error ? <span style={{ color: '#b91c1c', fontSize: 11 }}>{t.error}</span> : fmt(hit)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {chosen.length > 0 && (
          <div style={{ fontSize: 12, color: '#b45309', marginTop: 8 }}>
            Total yang akan dihapus: <b>{fmt(affectedRows(gkey))}</b> baris dari {chosen.length} tabel.
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <Alert msg={alert?.msg} type={alert?.type} onClose={() => setAlert(null)} />

      <div style={{ fontSize: 12, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px', marginBottom: 14 }}>
        <b>Tab destruktif.</b> Data SAP Role Maintenance dapat ditarik ulang lewat sync,
        tapi tabel log/history <b>tidak dapat dipulihkan</b> kecuali dari backup harian
        di <code>/backup/dashboard-db/</code>. Pastikan backup terverifikasi sebelum purge besar.
      </div>

      {/* Filter */}
      <div style={{ ...card, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 4 }}>LANDSCAPE / KONEKSI</div>
          <select value={connId} onChange={e => setConnId(e.target.value)}
            style={{ padding: '7px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6, minWidth: 240, fontFamily: 'inherit' }}>
            <option value="">🌐 Semua landscape</option>
            {conns.map(c => <option key={c.id} value={c.id}>{connLabel(c)}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 4 }}>LEBIH TUA DARI (HARI)</div>
          <input type="number" min={0} value={days}
            onChange={e => setDays(Math.max(0, Number(e.target.value) || 0))}
            style={{ padding: '7px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6, width: 120, fontFamily: 'inherit' }} />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>0 = semua umur. Hanya berlaku pada tabel berkolom tanggal.</div>
        </div>
        <Btn size="sm" disabled={!!busy} onClick={loadCat}>{busy === 'cat' ? '⏳' : '🔄'} Refresh hitungan</Btn>
        <Btn size="sm" disabled={!!busy} onClick={runVacuum}>{busy === 'vacuum' ? '⏳ VACUUM…' : '🧹 VACUUM'}</Btn>
      </div>

      <GroupCard gkey="saprm" icon="🔑" />
      <GroupCard gkey="other" icon="📦" />

      {/* Modal konfirmasi */}
      {confirmFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '20px 22px', width: 520, maxWidth: '92vw', maxHeight: '86vh', overflowY: 'auto' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#b91c1c', marginBottom: 10 }}>⚠️ Konfirmasi Purge</div>
            <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.7 }}>
              <div>Grup: <b>{cat?.groups?.[confirmFor.group]?.label}</b></div>
              <div>Landscape: <b>{curConnLabel()}</b></div>
              <div>Umur: <b>{days > 0 ? `lebih tua dari ${days} hari` : 'semua umur'}</b></div>
              <div>Tabel: <b>{confirmFor.tables.length}</b></div>
              <div>Perkiraan baris terhapus: <b style={{ color: '#b91c1c' }}>{fmt(affectedRows(confirmFor.group))}</b></div>
            </div>
            <div style={{ marginTop: 10, maxHeight: 150, overflowY: 'auto', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontFamily: 'ui-monospace, monospace', color: '#475569' }}>
              {confirmFor.tables.map(n => <div key={n}>{n}</div>)}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', margin: '14px 0 6px' }}>
              Ketik <b style={{ color: '#b91c1c' }}>PURGE</b> untuk melanjutkan:
            </div>
            <input value={confirmTxt} onChange={e => setConfirmTxt(e.target.value)} autoFocus
              placeholder="PURGE"
              style={{ width: '100%', padding: '8px 10px', fontSize: 14, border: '1px solid #cbd5e1', borderRadius: 6, fontFamily: 'inherit', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <Btn size="sm" disabled={busy === 'purge'} onClick={() => setConfirmFor(null)}>Batal</Btn>
              <Btn size="sm" variant="danger"
                disabled={busy === 'purge' || confirmTxt.trim().toUpperCase() !== 'PURGE'}
                onClick={doPurge}>
                {busy === 'purge' ? '⏳ Menghapus…' : '🗑️ Hapus sekarang'}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* Hasil */}
      {result && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 10 }}>📄 Hasil Purge</div>
          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={th}>Tabel</th>
                <th style={{ ...th, textAlign: 'right' }}>Dihapus</th>
                <th style={th}>Status</th>
              </tr></thead>
              <tbody>
                {result.results.map(r => (
                  <tr key={r.table}>
                    <td style={{ ...td, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{r.table}</td>
                    <td style={tdN}>{fmt(r.deleted)}</td>
                    <td style={{ ...td, color: r.ok ? '#15803d' : '#b91c1c' }}>{r.ok ? '✅ OK' : '⚠️ ' + (r.error || 'gagal')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 10 }}>
            Ukuran file DB belum berkurang sampai <b>VACUUM</b> dijalankan.
          </div>
        </div>
      )}
    </div>
  );
}

// ── AI Events ────────────────────────────────────────────────────────────────
// Kejadian anomali AI Service (tabel ai_events di ai_service.db, dibaca lewat
// proxy /api/ai-events). BUKAN log trafik: permintaan normal yang sukses tidak
// masuk sini.
//
// Alasan tab ini ada: semua diagnosa penting selama ini hanya hidup di
// `pm2 logs`, sementara urutan deploy standar adalah `pm2 delete ai-service`
// — yang menghapus log. Jadi bukti selalu hilang tepat saat berkas yang mau
// dibuktikan baru dipasang.
//
// String sengaja Inggris hardcoded, mengikuti tab m365/sdp/purge yang juga
// tidak punya kunci `tm.tab.*`. Menambah ~40 kunci ke translations.js (662 KB)
// di tiga blok bahasa tanpa bisa memverifikasi salinan live-nya adalah risiko
// build yang tidak sepadan untuk panel admin-only.

// Warna per severity. Dipakai di badge DAN di garis kiri baris tabel supaya
// baris 'error' bisa ditemukan dengan menyapu mata, tanpa membaca kolom.
const EV_SEV_COLOR = { error: '#dc2626', warn: '#d97706', info: '#64748b' };

// Label ramah + penjelasan SATU KALIMAT per jenis. Penjelasan ini yang membuat
// tabel bisa dibaca berminggu kemudian oleh orang yang sudah lupa konteksnya —
// tanpa itu, `tool_ceiling_hit` hanya string yang harus dicari artinya di kode.
const EV_META = {
  tool_ceiling_hit:  { label: 'Tool ceiling hit', icon: '🧗',
    hint: 'The Coding loop used its full tool_max_iter budget. Not automatically a problem — a model that calls tools in parallel can finish a complete answer at the ceiling.' },
  empty_answer:      { label: 'Empty answer', icon: '🕳️',
    hint: 'The model returned no text at all and the safety-net sentence was shown instead. Check tools_used: many tools with zero text points to model behaviour rather than a parsing loss.' },
  upstream_throttle: { label: 'Upstream throttle', icon: '🚦',
    hint: 'Provider returned 429 or 503. retry_after_ms = 0 means the wait time was NOT advertised — not "wait zero".' },
  provider_error:    { label: 'Provider error', icon: '⛔',
    hint: 'A non-throttle failure (400/401/5xx). Repeated 401 or 400 means broken configuration, not bad luck.' },
  key_rotation:      { label: 'Key rotation', icon: '🔁',
    hint: 'One key failed and the next was tried. Only logged when another key actually exists.' },
  tool_error:        { label: 'Tool error', icon: '🔧',
    hint: 'A single tool call failed. A run ending in several failures usually means the model was scraping leftovers, not running out of room.' },
  vision_request:    { label: 'Vision request', icon: '🖼️',
    hint: 'Images were attached. The server-side guard checks the PROVIDER, not the model — a model without vision still passes and the images are dropped silently.' },
  cache_event:       { label: 'Cache', icon: '📦',
    hint: 'A semantic-cache decision. Note read_bypassed: those requests never consulted the cache at all — that is a different thing from a miss, and it used to leave no trace.' },
};

// Outcome cache dikelompokkan supaya warnanya membawa arti saat menyapu tabel:
// hijau = LLM tidak dipanggil, biru = entri bertambah, abu = keputusan normal
// yang tidak menghasilkan apa-apa, merah = cache gagal menjalankan tugasnya.
const CACHE_OUTCOME_COLOR = {
  hit_exact: '#059669', hit_semantic: '#059669',
  write_stored: 'rgb(var(--brand-600))',
  near_miss: '#64748b', miss_empty_pool: '#64748b', write_skipped: '#64748b', read_bypassed: '#64748b',
  miss_no_vectors: '#dc2626', read_embed_failed: '#dc2626',
  write_embed_failed: '#dc2626', write_failed: '#dc2626', hit_exec_failed: '#dc2626',
};

function AiEventsPanel({ api }) {
  const [alert, setAlert]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [stats, setStats]     = useState(null);
  const [expanded, setExpanded] = useState(null);

  // Filter
  const [fType, setFType]   = useState('');
  const [fSev, setFSev]     = useState('');
  const [fProv, setFProv]   = useState('');
  const [fQ, setFQ]         = useState('');
  const [fDays, setFDays]   = useState('7');
  const [page, setPage]     = useState(0);
  const PAGE = 100;

  // `q` sengaja TIDAK masuk dependency load(): mengetik di kotak cari akan
  // memanggil API pada setiap huruf. Pencarian dijalankan lewat tombol/Enter.
  const [qApplied, setQApplied] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (fType) p.set('event_type', fType);
      if (fSev)  p.set('severity', fSev);
      if (fProv) p.set('provider', fProv);
      if (qApplied) p.set('q', qApplied);
      if (fDays !== 'all') {
        p.set('since', new Date(Date.now() - Number(fDays) * 86400000).toISOString());
      }
      p.set('limit', String(PAGE));
      p.set('offset', String(page * PAGE));
      const d = await api(`/api/ai-events?${p.toString()}`);
      setRows(d?.rows || []);
      setTotal(d?.total || 0);
    } catch (e) {
      setAlert({ type: 'error', msg: e.message });
      setRows([]); setTotal(0);
    } finally { setLoading(false); }
  }, [api, fType, fSev, fProv, qApplied, fDays, page]);

  const loadStats = useCallback(async () => {
    try {
      const days = fDays === 'all' ? 3650 : Number(fDays);
      setStats(await api(`/api/ai-events/stats?days=${days}`));
    } catch (_) { /* kartu ringkasan opsional — jangan jatuhkan tabel */ }
  }, [api, fDays]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // Mengganti filter harus mengembalikan ke halaman 1. Tanpa ini, filter yang
  // menyisakan 3 baris sementara page masih 4 menghasilkan tabel kosong yang
  // terlihat seperti "tidak ada data".
  const setFilter = (fn) => (v) => { fn(v); setPage(0); };

  const th   = { padding: '8px 10px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#334155', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
  const td   = { padding: '7px 10px', textAlign: 'left', fontSize: 13, color: '#0f172a', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };
  const sel  = { ...inputStyle, width: 'auto', minWidth: 130, padding: '6px 8px' };

  // ts disimpan ISO UTC. Tanpa konversi eksplisit, jam yang tampil meleset 7
  // jam dari jam operator — dan pada tabel yang gunanya justru mencocokkan
  // kejadian dengan waktu deploy, itu fatal.
  const fmtTs = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    try {
      const p = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Jakarta', hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      }).formatToParts(d).reduce((o, x) => (o[x.type] = x.value, o), {});
      return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`;
    } catch (_) { return String(iso); }
  };

  // detail_json dikirim MENTAH dari server. Satu baris dengan JSON cacat
  // (mis. sisa dari bug lama) tidak boleh menjatuhkan seluruh halaman.
  const parseDetail = (s) => { try { return s ? JSON.parse(s) : null; } catch (_) { return { _unparsed: String(s).slice(0, 500) }; } };

  // Ringkasan satu baris — bagian detail yang paling sering dicari, supaya
  // tidak perlu membuka setiap baris untuk tahu apakah ia menarik.
  const summarize = (r) => {
    const d = parseDetail(r.detail_json);
    if (!d) return '—';
    switch (r.event_type) {
      case 'tool_ceiling_hit':
        return `${d.tools_used} tools / ${d.iter_max} iter · answer ${d.answer_chars} chars · note ${d.note_shown ? 'shown' : 'suppressed'}`;
      case 'upstream_throttle':
        return `${d.status} · retry-after ${d.retry_after_known ? d.retry_after_ms + 'ms' : 'NOT SENT'}`;
      case 'provider_error':
        return `${d.status} · ${String(d.message || '').slice(0, 90)}`;
      case 'key_rotation':
        return `key ${d.from_key}→${d.to_key} of ${d.key_count} · ${d.status}`;
      case 'tool_error':
        return `${d.tool} · iter ${d.iter}/${d.iter_max} · ${String(d.error || '').slice(0, 70)}`;
      case 'empty_answer':
        return `${d.tools_used} tools used, ${d.tools_failed} failed · ${d.completion_tokens} completion tokens`;
      case 'vision_request':
        return `${d.n_images} image(s) · ${Math.round((d.approx_bytes || 0) / 1024)} KB`;
      case 'cache_event': {
        // Tiga dimensi kolam selalu ikut ditampilkan. Itulah yang membuat dua
        // baris bisa dibandingkan: entri yang tersimpan di bawah chart=1 tidak
        // akan pernah cocok dengan pertanyaan chart=0, dan tanpa kolom ini
        // perbedaan itu tak terlihat.
        const pool = `${d.source || '?'}/${d.lang || '-'}/chart${d.wants_chart ? 1 : 0}`;
        if (d.outcome === 'read_bypassed')   return `${pool} · never consulted — ${d.reason}`;
        if (d.outcome === 'hit_exact')       return `${pool} · exact hit · entry ${d.entry_id}`;
        if (d.outcome === 'hit_semantic')    return `${pool} · semantic hit ${d.score} · entry ${d.entry_id}`;
        if (d.outcome === 'near_miss')       return `${pool} · ${d.score} < ${d.threshold} · ${d.candidates} candidate(s)`;
        if (d.outcome === 'miss_empty_pool') return `${pool} · pool empty`;
        if (d.outcome === 'write_stored')    return `${pool} · stored${d.has_vector ? '' : ' (exact-only, no vector)'}`;
        if (d.outcome === 'write_skipped')   return `${pool} · not stored — ${d.reason}${d.historical ? ' [but SQL IS historical]' : ''}`;
        return `${pool} · ${d.outcome}${d.error ? ' — ' + String(d.error).slice(0, 60) : ''}`;
      }
      default:
        return String(r.detail_json || '').slice(0, 100);
    }
  };

  const meta = (t) => EV_META[t] || { label: t, icon: '•', hint: '' };
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div>
      {alert && <Alert msg={alert.msg} type={alert.type} onClose={() => setAlert(null)} />}

      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, lineHeight: 1.6 }}>
        Anomalies recorded by the AI Service. Successful ordinary requests are <b>not</b> listed here —
        those live in AI Chat History. Retention is 120 days.
        {stats?.oldest_ts && <> Oldest entry: <b>{fmtTs(stats.oldest_ts)}</b>.</>}
      </div>

      {/* Ringkasan per jenis dalam jendela terpilih */}
      {stats?.by_type?.length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          {Object.entries(
            stats.by_type.reduce((acc, r) => {
              acc[r.event_type] = (acc[r.event_type] || 0) + r.n;
              return acc;
            }, {})
          ).sort((a, b) => b[1] - a[1]).map(([type, n]) => (
            <div
              key={type}
              onClick={() => { setFType(fType === type ? '' : type); setPage(0); }}
              title={meta(type).hint}
              style={{
                cursor: 'pointer',
                background: fType === type ? 'rgb(var(--brand-50))' : '#f8fafc',
                border: `1px solid ${fType === type ? '#93c5fd' : '#e2e8f0'}`,
                borderRadius: 10, padding: '10px 14px', minWidth: 120,
              }}
            >
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, fontWeight: 600 }}>
                {meta(type).icon} {meta(type).label}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>{n}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <select style={sel} value={fDays} onChange={e => setFilter(setFDays)(e.target.value)}>
          <option value="1">Last 24 hours</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="120">Last 120 days</option>
          <option value="all">All</option>
        </select>
        <select style={sel} value={fType} onChange={e => setFilter(setFType)(e.target.value)}>
          <option value="">All types</option>
          {(stats?.types || []).map(t => <option key={t} value={t}>{meta(t).label}</option>)}
        </select>
        <select style={sel} value={fSev} onChange={e => setFilter(setFSev)(e.target.value)}>
          <option value="">All severities</option>
          <option value="error">error</option>
          <option value="warn">warn</option>
          <option value="info">info</option>
        </select>
        <select style={sel} value={fProv} onChange={e => setFilter(setFProv)(e.target.value)}>
          <option value="">All providers</option>
          {(stats?.providers || []).map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <input
          style={{ ...inputStyle, width: 'auto', minWidth: 200, padding: '6px 8px' }}
          placeholder="Search model / label / detail…"
          value={fQ}
          onChange={e => setFQ(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setQApplied(fQ); setPage(0); } }}
        />
        <Btn size="sm" onClick={() => { setQApplied(fQ); setPage(0); }}>🔍 Search</Btn>
        <Btn size="sm" onClick={() => {
          setFType(''); setFSev(''); setFProv(''); setFQ(''); setQApplied(''); setFDays('7'); setPage(0);
        }}>Reset</Btn>
        <Btn size="sm" onClick={() => { load(); loadStats(); }}>🔄 Refresh</Btn>
      </div>

      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
        {loading ? 'Loading…' : `${total.toLocaleString()} event(s) · page ${page + 1} of ${pages}`}
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...th, width: 20 }}></th>
            <th style={th}>Time (WIB)</th>
            <th style={th}>Event</th>
            <th style={th}>Model</th>
            <th style={th}>Label</th>
            <th style={th}>Summary</th>
          </tr></thead>
          <tbody>
            {rows.length === 0
              ? <tr><td style={td} colSpan={6}>{loading ? '…' : 'No events in this range. That is a good sign, not a broken page.'}</td></tr>
              : rows.map(r => {
                const m = meta(r.event_type);
                // Untuk cache_event, severity hampir selalu 'info' dan karena itu
                // tidak membedakan apa pun. Warna diambil dari outcome supaya
                // hit, skip, dan gagal bisa dibedakan tanpa membaca teksnya.
                let col = EV_SEV_COLOR[r.severity] || '#64748b';
                if (r.event_type === 'cache_event' && CACHE_OUTCOME_COLOR[r.label]) {
                  col = CACHE_OUTCOME_COLOR[r.label];
                }
                const open = expanded === r.id;
                return (
                  // Fragment BERKUNCI, bukan `<>`: shorthand tidak bisa
                  // menerima key, dan setiap baris di sini merender DUA <tr>
                  // (ringkas + detail). Tanpa key di elemen terluar, React
                  // memakai ulang baris yang salah saat filter diganti —
                  // gejalanya baris detail yang terbuka menempel pada event
                  // yang berbeda.
                  <Fragment key={r.id}>
                    <tr
                      onClick={() => setExpanded(open ? null : r.id)}
                      style={{ cursor: 'pointer', background: open ? '#f8fafc' : undefined }}
                    >
                      <td style={{ ...td, borderLeft: `3px solid ${col}`, padding: '7px 4px', color: '#94a3b8' }}>{open ? '▾' : '▸'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>{fmtTs(r.ts)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <span title={m.hint}>{m.icon} {m.label}</span>{' '}
                        <Badge text={r.severity} color={col} />
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12 }}>{r.model || '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12 }}>{r.label || '—'}</td>
                      <td style={{ ...td, fontSize: 12, color: '#475569' }}>{summarize(r)}</td>
                    </tr>
                    {open && (
                      <tr>
                        <td style={{ ...td, borderLeft: `3px solid ${col}` }} colSpan={6}>
                          {m.hint && (
                            <div style={{ fontSize: 12, color: '#475569', marginBottom: 8, fontStyle: 'italic' }}>{m.hint}</div>
                          )}
                          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                            provider: <b>{r.provider || '—'}</b> · app: <b>{r.app_id || '—'}</b> ·
                            {' '}user: <b>{r.user_id || '—'}</b> · session: <b>{r.session_id || '—'}</b>
                          </div>
                          <pre style={{
                            margin: 0, padding: 10, background: '#0f172a', color: '#e2e8f0',
                            borderRadius: 8, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap',
                          }}>
                            {JSON.stringify(parseDetail(r.detail_json), null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
          <Btn size="sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>← Prev</Btn>
          <span style={{ fontSize: 12, color: '#64748b' }}>{page + 1} / {pages}</span>
          <Btn size="sm" disabled={page + 1 >= pages} onClick={() => setPage(p => p + 1)}>Next →</Btn>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { key: 'users',   label: 'Users',          icon: '👥', roles: ['admin'],           component: UsersPanel },
  { key: 'logs',    label: 'Activity Logs',  icon: '📋', roles: ['admin'],           component: LogsPanel  },
  { key: 'tickets', label: 'Solman Tickets', icon: '🎫', roles: ['SAP', 'admin'],    component: TicketsPanel },
  { key: 'sap',     label: 'SAP Usage',      icon: '📊', roles: ['SAP', 'admin'],    component: SapPanel   },
  { key: 'billing', label: 'AWS Billing',    icon: '💳', roles: ['AWS', 'admin'],    component: BillingPanel },
  { key: 'aws',     label: 'AWS Cost',       icon: '☁️', roles: ['AWS', 'admin'],    component: AwsPanel   },
  { key: 'idsuim',  label: 'ID SUIM',        icon: '🔑', roles: ['admin'],           component: IdSuimPanel },
  { key: 'uploads', label: 'Upload History', icon: '🗂️', roles: ['admin'],           component: UploadHistoryPanel },
  { key: 'sapcfg',  label: 'SAP Config',    icon: '⚙️', roles: ['admin'],           component: SapConfigPanel },
  { key: 'sql',      label: 'SQL Editor',     icon: '🛢️', roles: ['admin'],           component: SqlEditorPanel },
  { key: 'database', label: 'Database',       icon: '🗄️', roles: ['admin'],           component: DatabasePanel },
  { key: 'overtime', label: 'Employee Overtime', icon: '⏱️', roles: ['admin'],        component: OvertimePanel },
  { key: 'asealert', label: 'ASE Alert',     icon: '🚨', roles: ['admin'],           component: AseAlertPanel },
  { key: 'sybasedr', label: 'Sybase DR',    icon: '🖥️', roles: ['admin'],           component: SybaseDrPanel },
  { key: 'm365',     label: 'M365 License',  icon: '🪟', roles: ['admin'],           component: M365Panel },
  { key: 'sdp',      label: 'SDP Cloud',     icon: '🛠️', roles: ['admin'],           component: SdpPanel },
  { key: 'purge',    label: 'Purge Data',    icon: '🧹', roles: ['admin'],           component: PurgePanel },
  { key: 'aievents', label: 'AI Events',     icon: '📡', roles: ['admin'],           component: AiEventsPanel },
];

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function TableMaintenance() {
  const { user } = useAuth();
  const api = useApi();
  const { t } = useLang();
  const [searchParams] = useSearchParams();

  const allowed = TABS.filter(t => t.roles.includes(user?.role));
  const [activeKey, setActiveKey] = useState(() => {
    const p = searchParams.get('tab');
    const match = allowed.find(tb => tb.key === p);
    return match ? match.key : (allowed[0]?.key || '');
  });
  // Validasi tetap terhadap `allowed` (tab yang boleh untuk peran user), bukan
  // TABS penuh — tanpa itu, deep link ke tab admin akan memasang activeKey yang
  // tidak punya panel untuk user biasa dan isinya jadi kosong.
  useTabDeepLink('table_maintenance', (p) => allowed.some(tb => tb.key === p), setActiveKey);

  const ActivePanel = TABS.find(t => t.key === activeKey)?.component;

  return (
    <div style={{ padding: '1.5rem', fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}>

      {/* Page Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>🗄️ {t('tm.title')}</h2>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 4, marginBottom: 0 }}>
          {t('tm.subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: '1.25rem', flexWrap: 'wrap', borderBottom: '1px solid #e2e8f0', paddingBottom: 0 }}>
        {allowed.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveKey(tab.key)}
            style={{
              padding: '9px 16px',
              fontSize: 13,
              cursor: 'pointer',
              border: 'none',
              borderBottom: activeKey === tab.key ? '2px solid rgb(var(--brand-600))' : '2px solid transparent',
              background: 'none',
              color: activeKey === tab.key ? 'rgb(var(--brand-600))' : '#64748b',
              fontWeight: activeKey === tab.key ? 700 : 500,
              fontFamily: 'inherit',
              transition: 'all 0.15s',
              marginBottom: -1,
              whiteSpace: 'nowrap',
            }}
          >
            {tab.icon} {(() => { const tk = 'tm.tab.' + tab.key; const tl = t(tk); return (!tl || tl === tk) ? tab.label : tl; })()}
          </button>
        ))}
      </div>

      {/* Panel Content */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        {ActivePanel ? <ActivePanel api={api} /> : <Empty icon="🔒" text={t('tm.noAccess')} />}
      </div>
    </div>
  );
}
